import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "../ui/button"
import { IconButton } from "../ui/icon-button"
import { Input } from "../ui/input"
import { NavigationButtons } from "../ui/navigation-buttons"
import { extractProjectIdFromUrl, getCurrentTabSlotId, isChatConversationTabId, isMarkdownProductBoardFilePath, isNextcloudDocumentTab, resolveToolbarDocumentPath, resolveViewerRefreshAction, shouldClearDocumentPathForTab, shouldHideDocumentFileActions, shouldSkipDocumentPathFetch, shouldSkipExternalDocumentPolling } from "./document-viewer.utils"
import type { InfoDockMode } from "../features/document-info-dock"
import { detectBasecampRecordType, resolveBasecampAppUrl, resolveExternalBasecampUrlForDocument, buildBasecampWebUrl } from "@/lib/basecamp-external-url"
import { DocumentToolbarBreadcrumbs } from "./document-toolbar-breadcrumbs"
import { FlowRuntimeControl } from "@/components/features/flows/flow-runtime-control"
import { NextcloudDocumentCrumbs } from "@/components/integrations/nextcloud/NextcloudDocumentCrumbs"
import { setNextcloudShellNav } from "@/lib/nextcloud-integration-nav"
import { BasecampIntegrationBreadcrumbs } from "../integrations/basecamp/components/basecamp-integration-breadcrumbs"
import { shouldShowBasecampChromeBreadcrumbs } from "@/lib/basecamp-breadcrumbs"
import { testIds } from "../../lib/test-ids"
import { BasecampIcon } from "@/components/icons/basecamp-icon"
import { Maximize2, MoreVertical, ChevronRight, Info, Minimize2, Loader2, FolderOpen, Save, Trash2, Undo, Folder, Pencil, MessageSquare, FileDown, Bookmark, BookmarkX, ExternalLink, RefreshCw, Code2, Archive, ArchiveRestore, CheckCircle, CheckCircle2, Link, List, PanelLeft, Pin, PinOff, CloudUpload, CloudDownload, Upload, Unlink, Search, Settings, Volume2 } from "lucide-react"
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from "@/lib/add-to-current-chat"
import { useWorkspace } from "@/providers/workspace-provider"
import { isDocumentDeepLinkRestorePending } from "@/lib/document-deep-link-pending"
import { useWorkspaceExtensions } from "@/hooks/use-workspace-extensions"
import { FolderPickerDialog } from "../features/folder-picker-dialog"
import { RenameDialog } from "../features/rename-dialog"
import { DocumentList } from "../features/document-list"
import type { SelectedItem } from "../features/document-list"
import { DocumentPreviewPane } from "../features/document-list"
import { DocumentTabs, type OpenTab } from "../features/document-tabs"
import { useDocumentSearchBoostIds } from '@/hooks/use-document-search-boost-ids'
import { ChatConversationToolbar } from "@/components/features/chats/ChatConversationToolbar"
import { InboxThreadToolbar } from "@/components/features/inbox/InboxThreadToolbar"
import { InboxThreadChromeProvider } from "@/components/features/inbox/inbox-thread-chrome-context"
import { usePerTabNavigationHistory } from "../../hooks/use-navigation-history"
import { useKeyboardShortcuts } from "../../hooks/use-keyboard-shortcuts"
import { useContainerWidth } from "../../hooks/use-container-width"
import { useDocumentOperations } from "../../hooks/use-document-operations"
import { useTabManagement } from "../../hooks/use-tab-management"
import { useViewMode } from "../../hooks/use-view-mode"
import { useExternalFileChangeDetection } from "../../hooks/use-external-file-change-detection"
import { resolveDocumentLink, fetchWithAuth, IS_LOCAL_API, getSidebarShortcuts, createSidebarShortcut, deleteSidebarShortcut, resolveDocumentRowId, generateDocumentTts, fetchDocumentTtsStatus, type SidebarShortcut } from "../../lib/api"
import { useOpenInCursor } from "@/hooks/use-open-in-cursor"
import { hrefForInAppDocumentNavigation } from "@/lib/maater-document-url"
import { dispatchCreateInFolder } from "@/lib/create-in-folder"
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
  QUIET_OVERFLOW_MENU_LABEL_CLASS,
} from "@/lib/menu-chrome"
import { isModClick } from "@/lib/open-intent"
import { markDocumentForEditOnOpen } from "@/lib/pending-document-edit-focus"
import { useIntegrations } from "../../hooks/use-integrations"
import { markTabClickNavigation, markProgrammaticNavigation } from "../../hooks/use-url-sync"
import {
  navigationFieldsFromIntegrationOpen,
  navigationFieldsFromOpenTab,
} from "@/lib/basecamp-integration-nav"
import { DrivePublishPicker } from "@/components/integrations/google-workspace/drive-publish-picker"
import { GoogleDriveIcon } from "@/components/icons/google-drive-icon"
import { DRIVE_PREVIEW_OPEN_IMPORT_EVENT } from "@/components/integrations/google-workspace/gdoc-import-utils"
import { OpenDestinationIcons, resolveOpenExternalIcon } from "@/lib/open-destination-icons"
import {
  BASECAMP_OPEN_IN_BASECAMP_LABEL,
  basecampViewTodoLabel,
} from "@/lib/basecamp-todo-open-labels"
import { useGdocSync } from "@/hooks/use-gdoc-sync"
import { useGoogleDriveAuth } from "@/hooks/use-google-drive-auth"
import { publishDocumentToGoogleDrive, unlinkDocumentFromGoogleDrive } from "@/lib/google-drive-sync-api"
import { DiffPreviewDialog } from "@/components/layout/diff-preview-dialog"
import { CursorWorkspaceDropdown } from "@/components/features/cursor-workspace-dropdown"
import { CursorWorkspaceMenu } from "@/components/features/cursor-workspace-menu"
import { GitPathConfig } from "@/components/integrations/basecamp/components/git-path-config"
import { useBasecampProjects } from "../../hooks/use-basecamp"
import { useExtensionSupport } from "../../hooks/use-extension-support"
import { toast } from "sonner"
import {
  getRendererCapabilities,
  resolveDocumentRendererKind,
} from "@/lib/document-renderer-registry"
import { downloadImageDocumentFile } from "@/lib/api/image-documents"
import { resolveBasecampIndexSection } from '@/lib/index-query-url'
import {
  dispatchIndexPanelSearch,
  dispatchIndexViewAction,
} from '@/lib/index-surface-actions'
import { panelSearchShortcutHint } from '@/lib/panel-search-menu'
import type { DocumentViewerProps, DocumentType } from "./document-viewer.types"
import { COMMAND_PALETTE_BASECAMP_URL_KEY, extractBasecampUrlInfo } from "./command-palette.utils"
import { resolveLinkageTodoId } from "@/lib/todo-id-from-path"
import { openBasecampTodoInFlowNotes } from "@/lib/basecamp-todo-integration"
import { extractHeadings, computeHeadingRatios } from "@/lib/heading-utils"
import type { DocHeading } from "@/lib/heading-utils"

import { fetchWithRetry, generateTabSlotId } from "./resizable-layout.utils"
import { WorkspaceLinkDialog } from "@/components/features/workspace-link-dialog"
import {
  isWorkspaceAgnosticTab,
  resolveTabWorkspaceId,
  resolveWorkspaceForDocument,
} from "./resizable-layout.handlers/shared"
import { BASECAMP_PROJECT_TODO_IN_PATH_RE } from "@/lib/basecamp-url"
import {
  buildAppUrl,
  pathToUrl,
  absoluteAppUrlForTab,
  formatUserFacingStoragePath,
  resolveStoragePathForFetch,
  isUuid,
  workspaceSlugForId,
} from "@/lib/app-routes"
import {
  folderLabelForCreatePath,
  resolveCreateFolderPath,
} from "@/lib/resolve-create-folder-path"
import { deskPathForMember } from "@/lib/workspace-desk-path"
import { useAccountDeskSlug } from "@/hooks/use-account-desk-slug"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useCloudDispatchForDocument } from "@/hooks/use-cloud-dispatch-for-document"
import { CloudDispatchBanner } from "@/components/features/agent/cloud-dispatch-banner"
import { AgentLiveStreamPanel } from "@/components/features/agent/agent-live-stream-panel"
import { SEARCH_VIEW_TAB_ID } from "@/lib/static-app-views"
import {
  PEOPLE_INDEX_TAB_ID,
  drillOpenTabToPerson,
  personViewTabId,
  personViewTabTitle,
  openPeopleIndexInTabs,
  resolvePersonWorkspaceId,
  type OpenPersonViewDetail,
} from "@/lib/person-view"
import { DocumentViewerMainContent } from "./document-viewer-main-content"
import { parentFolderFromDocumentPath } from "@/lib/local-text-import"
import {
  isArchivedDocument,
  parentFolderFromContentPath,
  resolveOriginalPathBeforeArchive,
} from "@/lib/document-archive"
import { DocumentSoftBookmarkMenuSection } from "./document-soft-bookmark-menu"

const DOC_UUID_RE = /^[0-9a-f-]{36}$/i
const OpenInMaaterIcon = OpenDestinationIcons.open
const OpenInBasecampIcon = resolveOpenExternalIcon(BasecampIcon)

export function DocumentViewer({
  onFullscreenToggle,
  isFullscreen = false,
  onDocumentDragToChat,
  onSendToChat,
  openTabs,
  setOpenTabs,
  activeTabId,
  setActiveTabId,
  tabBarActiveId,
  setTabBarActiveId,
  showDocumentList,
  onDocumentListToggle,
  selectedProjectId,
  workspaceColor,
  navigation: parentNavigation,
  onGoBack: parentGoBack,
  onGoForward: parentGoForward,
  onDocumentClick: parentDocumentClick,
  onShowFileLocation,
  onFolderClick,
  activeDocumentIdForFolderView,
  onTabShowFileLocation,
  onTabRename,
  onTabMove,
  onTabExportPDF,
  onTabAddToChat,
  onTabCloseOthers,
  onTabMoveToWindow,
  onTabCopyToWindow,
  getTabFilePath,
  onTabToggleBookmark,
  getTabBookmarkStatus,
  onUrlChange,
  onNativeSidebarExplorerDropOnTabBar,
  setSelectedProjectId,
  setShowDocumentList,
  getWorkspaceIdForPath,
}: DocumentViewerProps) {
  const { workspaces, activeWorkspaceIds, toggleWorkspace } = useWorkspace()

  /** Open doc belongs to a workspace currently toggled off in the sidebar switcher. */
  const inactiveWorkspaceForActiveDoc = useMemo(() => {
    if (!activeTabId || workspaces.length === 0) return null
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (!currentTab || isWorkspaceAgnosticTab(currentTab)) return null
    const wsId = resolveTabWorkspaceId(currentTab, workspaces)
    if (!wsId || activeWorkspaceIds.includes(wsId)) return null
    return workspaces.find((w) => w.id === wsId) ?? null
  }, [activeTabId, openTabs, workspaces, activeWorkspaceIds])

  const moveDialogWorkspaces = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        name: w.name,
        slug: w.slug,
        contentRoot: (w.contentRoot ?? '').trim(),
      })),
    [workspaces],
  )
  const { deskSlug } = useAccountDeskSlug()
  const [createFolderPickerOpen, setCreateFolderPickerOpen] = useState(false)
  const workspacesRoute = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces],
  )
  const { extensions: workspaceExtensions } = useWorkspaceExtensions(activeWorkspaceIds?.[0] ?? null)
  const cursorExtensionEnabled = workspaceExtensions.some((e) => e.extension_id === 'cursor' && e.enabled)

  // Use view mode hook
  const {
    viewMode,
    setViewMode,
    showInfoPane,
    setShowInfoPane,
    infoPanelRef,
    documentPanelSizes,
    setDocumentPanelSizes,
    handleCloseInfoPane,
  } = useViewMode()

  /** Details | History mode inside the Document Info dock (Info toolbar owns the rail). */
          const [infoDockMode, setInfoDockMode] = useState<InfoDockMode>('details')
  const handleCloseInfoDock = useCallback(() => {
    setInfoDockMode('details')
    handleCloseInfoPane()
  }, [handleCloseInfoPane])

  useEffect(() => {
    setInfoDockMode('details')
  }, [activeTabId])

  const fallbackNavigation = usePerTabNavigationHistory()
  const navigation = parentNavigation || fallbackNavigation
  const navigate = useNavigate()
  const location = useLocation()
  const [editingTitle, setEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState("")
  const titleInputRef = useRef<HTMLInputElement>(null)
  /** Skip onBlur save when Escape cancels inline title edit (unmount would otherwise save). */
  const skipTitleBlurSaveRef = useRef(false)
  const activeTabIdRef = useRef<string | null>(activeTabId)
  const toolbarRef = useRef<HTMLElement>(null)
  const containerWidth = useContainerWidth(toolbarRef)

  // Workspace link dialog (shown when Open in Cursor fails with project_not_linked)
  const [workspaceLinkDialogOpen, setWorkspaceLinkDialogOpen] = useState(false)
  const [workspaceLinkContext, setWorkspaceLinkContext] = useState<{ projectName: string; basecampProjectId?: string | null } | null>(null)

  // Sidebar shortcuts (for Pin to Sidebar in toolbar menu)
  const [sidebarShortcuts, setSidebarShortcuts] = useState<SidebarShortcut[]>([])
  useEffect(() => {
    getSidebarShortcuts().then(r => setSidebarShortcuts(r.shortcuts || [])).catch(() => {})
    const handler = () => {
      getSidebarShortcuts().then(r => setSidebarShortcuts(r.shortcuts || [])).catch(() => {})
    }
    window.addEventListener('sidebar-shortcuts-changed', handler)
    return () => window.removeEventListener('sidebar-shortcuts-changed', handler)
  }, [])

  // TOC + Overview Ruler state
  const [tocOpen, setTocOpen] = useState(false)
  const [tocHeadings, setTocHeadings] = useState<DocHeading[]>([])
  /** WYSIWYG vs Source for the open markdown document — used to hide TOC in raw Source view. */
  const [markdownBodyViewMode, setMarkdownBodyViewMode] = useState<"wysiwyg" | "source">("wysiwyg")
  const tocContentRef = useRef<HTMLDivElement>(null)
  const tocScrollRef = useRef<HTMLDivElement>(null)
  // The actual scrolling element inside DocumentContent (TipTap or CodeMirror)
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null)
  const scrollElRef = useMemo(
    () => ({ current: scrollEl }) as React.RefObject<HTMLElement>,
    [scrollEl]
  )

  const resolveWorkspaceIdForNewIntegrationTab = useCallback(
    (currentTabs: OpenTab[], detailWorkspaceId?: string | null): string | undefined => {
      if (detailWorkspaceId) return detailWorkspaceId
      const activeId = activeTabIdRef.current
      const sourceTab = activeId ? currentTabs.find((t) => t.id === activeId) : undefined
      if (sourceTab?.workspaceId) return sourceTab.workspaceId ?? undefined
      if (sourceTab?.type === 'explorer' && sourceTab.explorerData?.folderPath) {
        const fromPath = resolveWorkspaceForDocument(sourceTab.explorerData.folderPath, workspaces)
        if (fromPath) return fromPath
      }
      const firstActiveWork = activeWorkspaceIds
        .map((id) => workspaces.find((w) => w.id === id))
        .find((w) => w && w.type === 'work')
      return firstActiveWork?.id
    },
    [workspaces, activeWorkspaceIds]
  )

  // Keep ref in sync with prop
  useEffect(() => {
    activeTabIdRef.current = activeTabId
  }, [activeTabId])

  // Define handleDocumentClick early so it can be used in the hook
  const handleDocumentClick = useCallback(async (documentId: string, openInNewTab = false) => {
    // Use parent's handler if available (has proper tab switching logic)
    if (parentDocumentClick) {
      // Convert DocumentList's signature (documentId, openInNewTab) to parent's signature (documentId, event)
      const syntheticEvent = openInNewTab
        ? ({
            metaKey: true,
            ctrlKey: false,
            preventDefault: () => {},
            stopPropagation: () => {}
          } as React.MouseEvent)
        : undefined

      await parentDocumentClick(documentId, syntheticEvent)
      return
    }

    // Fallback to local implementation if parent handler not available
    const existingTab = openTabs.find(tab => tab.id === documentId)

    if (openInNewTab) {
      // CMD+click or explicit new tab request: Create new tab OR switch to existing
      if (existingTab) {
        // Document already open, just switch to it (browser behavior)
        setActiveTabId(documentId)
      } else {
        // Document not open, create new tab
        try {
          let apiUrl: string
          if (documentId.includes('/')) {
            apiUrl = `/api/documents/by-path?path=${encodeURIComponent(documentId)}`
          } else {
            apiUrl = `/api/documents/${documentId}`
          }

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const docData = await response.json()
            setOpenTabs([...openTabs, {
              id: documentId,
              title: docData.title,
              type: docData.type,
              isDirty: false,
            }])
            setActiveTabId(documentId)
          } else {
            throw new Error(`Failed to load document: ${response.statusText}`)
          }
        } catch (error) {
          console.error('Failed to fetch document:', error)
          setOpenTabs([...openTabs, {
            id: documentId,
            title: `Document ${documentId}`,
            type: 'note' as const,
            isDirty: false,
          }])
          setActiveTabId(documentId)
        }
      }
    } else if (existingTab) {
      // Normal click + already open: Switch to existing tab
      setActiveTabId(documentId)
    } else {
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (currentTab?.type === 'explorer') {
        // Normal click from Explorer view: Create new tab
        try {
          let apiUrl: string
          if (documentId.includes('/')) {
            apiUrl = `/api/documents/by-path?path=${encodeURIComponent(documentId)}`
          } else {
            apiUrl = `/api/documents/${documentId}`
          }

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const docData = await response.json()
            setOpenTabs([...openTabs, {
              id: documentId,
              title: docData.title,
              type: docData.type,
              isDirty: false,
            }])
            setActiveTabId(documentId)
          } else {
            throw new Error(`Failed to load document: ${response.statusText}`)
          }
        } catch (error) {
          console.error('Failed to fetch document:', error)
          setOpenTabs([...openTabs, {
            id: documentId,
            title: `Document ${documentId}`,
            type: 'note' as const,
            isDirty: false,
          }])
          setActiveTabId(documentId)
        }
      } else {
        // Normal click with existing active tab: Replace it (browser-style navigation)
        try {
        let apiUrl: string
        if (documentId.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(documentId)}`
        } else {
          apiUrl = `/api/documents/${documentId}`
        }

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const docData = await response.json()
            const updatedTabs = openTabs.map(tab =>
              tab.id === activeTabId
                ? { id: documentId, title: docData.title, type: docData.type, isDirty: false }
                : tab
            )
            setOpenTabs(updatedTabs)
          } else {
            throw new Error(`Failed to load document: ${response.statusText}`)
          }
        } catch (error) {
          console.error('Failed to fetch document:', error)
          const updatedTabs = openTabs.map(tab =>
            tab.id === activeTabId
              ? { id: documentId, title: `Document ${documentId}`, type: 'note' as const, isDirty: false }
              : tab
          )
          setOpenTabs(updatedTabs)
        }
      }
    }

    setActiveTabId(documentId)
    setViewMode("single")

    // Add to navigation history
    // NOTE: Suspected Bug 1 — documentId is used as the slot key here. The
    // back button looks up history by `tabSlotId`, so these entries are
    // orphaned. Tagged "doc-viewer-doc-click-legacy-by-doc-id".
    navigation.navigate(documentId, { documentId, viewMode: "single" }, "doc-viewer-doc-click-legacy-by-doc-id")
  }, [parentDocumentClick, openTabs, setOpenTabs, activeTabId, setActiveTabId, setViewMode, navigation])

  // Adapter for components that expect MouseEvent signature (or boolean for some components)
  const handleDocumentClickWithEvent = useCallback(async (documentId: string, eventOrBoolean?: React.MouseEvent | boolean) => {
    // Handle both MouseEvent and boolean types
    const openInNewTab = typeof eventOrBoolean === 'boolean'
      ? eventOrBoolean
      : (eventOrBoolean?.metaKey || eventOrBoolean?.ctrlKey || false)
    await handleDocumentClick(documentId, openInNewTab)
  }, [handleDocumentClick])

  const { openTabIds: searchBoostOpenTabIds, recentlyViewedIds: searchBoostRecentlyViewedIds } =
    useDocumentSearchBoostIds(openTabs)

  // Use tab management hook
  const {
    handleTabClick,
    handleTabClose,
    handleTabReorder,
    handleTabPinToggle,
    handleTabDragToChat,
  } = useTabManagement({
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    setTabBarActiveId,
    setViewMode,
    navigation,
    onDocumentDragToChat,
  })

  // Use document operations hook
  const documentOperations = useDocumentOperations({
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    onDocumentDragToChat,
    onShowFileLocation,
    parentDocumentClick,
    handleDocumentClick,
    navigation,
    getWorkspaceIdForPath,
    setSelectedProjectId,
    setShowDocumentList,
    workspacesRoute,
  })

  // Extract state and handlers from hook
  const {
    currentDocumentPath,
    setCurrentDocumentPath,
    documentSaveState,
    undoState,
    moveDialogOpen,
    renameDialogOpen,
    deleteDialogOpen,
    documentToDelete,
    setMoveDialogOpen,
    setRenameDialogOpen,
    setDeleteDialogOpen,
    handleRenameClick,
    handleRenameConfirm,
    handleMove,
    handleDeleteClick,
    handleDelete,
    handleShowFileLocation,
    handleManualSave,
    completeManualSave,
    handleUndo,
    handleExportPDF,
    handleAddToChatClick,
  } = documentOperations

  const activeTabForPolling = openTabs.find((tab) => tab.id === activeTabId)

  // External change detection: local filesystem (Cursor) + cloud API writers (maater-sync, MCP).
  // Content-revision poll (~4.5s) also wakes this hook via flownotes-content-revision.
  const externalChangeDetection = useExternalFileChangeDetection({
    documentId: activeTabId,
    workspaces: workspacesRoute,
    enabled: !shouldSkipExternalDocumentPolling(activeTabId, activeTabForPolling),
    // Local API: snappier (Cursor on disk). Cloud: slightly slower; revision events cover eagerness.
    checkInterval: IS_LOCAL_API ? 3000 : 10000,
    onChangeDetected: async (newModifiedAt) => {
      // Format time for toast notification
      const formatTimeAgo = (dateStr: string) => {
        const date = new Date(dateStr)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffSecs = Math.floor(diffMs / 1000)
        const diffMins = Math.floor(diffSecs / 60)

        if (diffSecs < 60) return 'just now'
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
        return date.toLocaleString()
      }

      // Check for conflicts (unsaved changes)
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      const hasUnsavedChanges = currentTab?.isDirty || false

      if (!hasUnsavedChanges) {
        // No unsaved changes - auto-refresh seamlessly!

        // Show subtle toast notification (no action needed)
        toast.info("Document refreshed", {
          description: `Updated ${formatTimeAgo(newModifiedAt)}`,
          duration: 3000, // Short duration since it's just informational
          id: `external-change-${activeTabId}` // Prevent duplicate toasts
        })

        // Auto-refresh immediately
        await externalChangeDetection.refreshDocument()
      } else {
        // User has unsaved changes - show warning (conflict detected!)
        toast.warning("Document changed externally", {
          description: "Save your changes first, then refresh to see external changes",
          action: {
            label: "Save & Refresh",
            onClick: async () => {
              await handleManualSave()
              await externalChangeDetection.refreshDocument()
            }
          },
          duration: 10000, // Longer duration for important conflict warning
          id: `external-change-conflict-${activeTabId}`
        })
      }
    }
  })

  // Handle external refresh with conflict checking
  // ========================================
  // Extension Support
  // ========================================

  // Get extension for current tab (if any)
  const { currentExtension, isCurrentTabExtension } = useExtensionSupport(
    openTabs.find(tab => tab.id === activeTabId) || null
  )

  // State for Basecamp bookmark
  const [basecampTodoId, setBasecampTodoId] = useState<string | null>(null)
  const [basecampProjectId, setBasecampProjectId] = useState<string | null>(null)
  const [projectName, setProjectName] = useState<string | null>(null)
  const [isBookmarked, setIsBookmarked] = useState<boolean | null>(null)
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false)

  // Dialog states are now managed by useDocumentOperations hook

  // Fetch Basecamp projects for breadcrumbs
  const { projects: basecampProjects } = useBasecampProjects()

  // Track Basecamp todo title visibility for breadcrumbs
  const [basecampTodoTitleVisible, setBasecampTodoTitleVisible] = useState(true)
  const [basecampTodoTitle, setBasecampTodoTitle] = useState<string | null>(null)

  // Reset title visibility when switching tabs
  useEffect(() => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId);
    if (currentTab?.type !== 'integration-basecamp-todo') {
      setBasecampTodoTitleVisible(true);
      setBasecampTodoTitle(null);
    }
  }, [activeTabId, openTabs]);

  // Fetch document path when activeTabId changes
  useEffect(() => {
    // Skip for special views that aren't documents
    const currentTab = openTabs.find(tab => tab.id === activeTabId);
    if (shouldClearDocumentPathForTab(activeTabId, currentTab)) {
      setCurrentDocumentPath(null)
      return
    }

    const pathHint = resolveToolbarDocumentPath(null, currentTab, activeTabId)
    setCurrentDocumentPath(pathHint)

    const fetchDocumentPath = async () => {
      // Skip fetching for special view IDs that aren't actual documents
      if (shouldSkipDocumentPathFetch(activeTabId, currentTab)) {
        return
      }

      const boardPathKey =
        activeTabId.includes('/')
          ? activeTabId
          : currentTab?.filePath ?? null
      if (boardPathKey && isMarkdownProductBoardFilePath(boardPathKey)) {
        return
      }

      try {
        let apiUrl: string
        if (activeTabId.includes('/')) {
          // Wait for org list so slug paths map to content_root (avoid slug 403 storms).
          if (workspacesRoute.length === 0) return
          const storagePath = resolveStoragePathForFetch(activeTabId, workspacesRoute)
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(storagePath)}`
        } else {
          apiUrl = `/api/documents/${activeTabId}`
        }

        const response = await fetchWithRetry(apiUrl, { retryOn404: true, max404Retries: 2 })
        if (response.ok) {
          const docData = await response.json()
          setCurrentDocumentPath(docData.filePath || docData.path)
          setDocumentDbId(docData.id || null)
          // Also fetch source_url for Basecamp button - check url/source_url first, then construct from todo_id
          const frontmatter = docData.frontmatter || {}
          setDocumentFrontmatter(frontmatter) // Store frontmatter for use in breadcrumbs
          if (docData.type === 'flow') {
            setOpenTabs((prev) => {
              const tab = prev.find((item) => item.id === activeTabId)
              if (!tab || tab.type === 'flow') return prev
              return prev.map((item) => (
                item.id === activeTabId ? { ...item, type: 'flow' } : item
              ))
            })
          }
          setDocumentLifecycleStatus(
            typeof docData.status === 'string'
              ? docData.status
              : typeof frontmatter.status === 'string'
                ? frontmatter.status
                : null,
          )
          setDocumentOriginalPath(
            typeof docData.original_path === 'string'
              ? docData.original_path
              : typeof docData.originalPath === 'string'
                ? docData.originalPath
                : typeof frontmatter.original_path === 'string'
                  ? frontmatter.original_path
                  : null,
          )
          setDocumentBodyForChangelog(docData.content || '')
          let sourceUrl = frontmatter.url || frontmatter.source_url || null
          // If no URL but todo_id exists, construct Basecamp URL
          const docPath = docData.filePath || docData.path
          const todoId = resolveLinkageTodoId(docPath, frontmatter)
          let projectId = frontmatter.project?.id || frontmatter.project_id

          try {
            const pendingPaletteUrl = sessionStorage.getItem(COMMAND_PALETTE_BASECAMP_URL_KEY)
            if (pendingPaletteUrl) {
              sessionStorage.removeItem(COMMAND_PALETTE_BASECAMP_URL_KEY)
              const pendingInfo = extractBasecampUrlInfo(pendingPaletteUrl)
              if (
                pendingInfo.todoId &&
                todoId &&
                pendingInfo.todoId === String(todoId)
              ) {
                if (!sourceUrl) sourceUrl = pendingPaletteUrl
                if (!projectId && pendingInfo.projectId) projectId = pendingInfo.projectId
              }
            }
          } catch {
            // ignore sessionStorage errors
          }

          // If projectId not in frontmatter, try to extract from source URL
          if (!projectId && sourceUrl) {
            const extractedProjectId = extractProjectIdFromUrl(sourceUrl)
            if (extractedProjectId) {
              projectId = extractedProjectId
            }
          }

          // When todo_id came from path (child doc in todo folder), projectId may be in a sibling doc
          if (todoId && !projectId) {
            try {
              const docsRes = await fetchWithAuth(`/api/basecamp/todo-documents/${todoId}?limit=5`)
              if (docsRes.ok) {
                const { documents } = await docsRes.json()
                const first = documents?.[0]
                const pid = first?.frontmatter?.project_id ?? first?.frontmatter?.project?.id
                if (pid) projectId = pid
                const url = first?.frontmatter?.source_url ?? first?.frontmatter?.url
                if (url && !sourceUrl) sourceUrl = url
              }
            } catch (_) {
              // ignore
            }
          }

          // Last resort: resolve via cached Basecamp todo (same pattern as document-info-pane)
          if (todoId && !projectId) {
            try {
              const searchRes = await fetchWithAuth(`/api/basecamp/search-todo/${todoId}`)
              if (searchRes.ok) {
                const searchJson = await searchRes.json()
                if (searchJson?.found && searchJson?.todo?.projectId) {
                  projectId = String(searchJson.todo.projectId)
                }
              }
            } catch (_) {
              // ignore
            }
          }

          if (!sourceUrl && todoId && projectId) {
            const recordType = detectBasecampRecordType(todoId, { frontmatter, filePath: docPath })
            sourceUrl = resolveBasecampAppUrl({ recordId: todoId, projectId, recordType })
          } else if (sourceUrl && todoId && projectId) {
            const recordType = detectBasecampRecordType(todoId, { frontmatter, filePath: docPath })
            sourceUrl = resolveBasecampAppUrl({
              recordId: todoId,
              projectId,
              recordType,
              candidateUrl: sourceUrl,
            })
          }
          setDocumentSourceUrl(sourceUrl)

          // Store Basecamp todo info for bookmark functionality
          setBasecampTodoId(todoId || null)
          setBasecampProjectId(projectId || null)

          // Store project name for workspace generation
          setProjectName(docData.projectName || null)
        } else if (response.status === 404) {
          // Document not found (likely deleted) - handle gracefully
          // Don't log as error, this is expected when files are deleted
          // UI will handle showing "Document not found" message
        }
      } catch (error) {
        // Only log non-404 errors
        if (!(error instanceof Error && error.message.includes('404'))) {
          console.error('Failed to fetch document path:', error)
        }
      }
    }

    fetchDocumentPath()
  }, [activeTabId, openTabs, workspacesRoute, setCurrentDocumentPath, setOpenTabs])

  const bookmarkStatusFetchKeyRef = useRef<string | null>(null)

  // Load bookmark status when Basecamp todo info is available
  useEffect(() => {
    const loadBookmarkStatus = async () => {
      const { isApiDbCircuitOpen } = await import('@/lib/api/core')
      if (isApiDbCircuitOpen()) {
        return
      }
      // Check if we're viewing a Basecamp todo tab
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (currentTab?.type === 'integration-basecamp-todo') {
        const todoId = currentTab.integrationData?.todoId || currentTab.id.replace('basecamp-todo-', '')
        let projectId = currentTab.integrationData?.projectId

        // Fallback: Extract projectId from URL if missing from integrationData
        if (!projectId || projectId === '') {
          const urlMatch = location.pathname.match(BASECAMP_PROJECT_TODO_IN_PATH_RE)
          if (urlMatch && urlMatch[1]) {
            projectId = urlMatch[1]

            // Update tab's integrationData with projectId for future use (use functional update to avoid dependency)
            setOpenTabs(prevTabs => {
              const tabExists = prevTabs.some(t => t.id === activeTabId && t.integrationData)
              if (!tabExists) return prevTabs

              return prevTabs.map(t => {
                if (t.id === activeTabId && t.integrationData) {
                  return {
                    ...t,
                    integrationData: {
                      ...t.integrationData,
                      projectId: projectId
                    }
                  }
                }
                return t
              })
            })
          }
        }

        if (!projectId || projectId === '' || !todoId) {
          setIsBookmarked(null)
          return
        }

        const fetchKey = `tab:${projectId}:${todoId}`
        if (bookmarkStatusFetchKeyRef.current === fetchKey) return
        bookmarkStatusFetchKeyRef.current = fetchKey

        try {
          const { basecampApi } = await import('@/lib/basecamp-api')
          const status = await basecampApi.getBookmarkStatus(projectId.toString(), todoId.toString())
          setIsBookmarked(status.bookmarked)
        } catch (err) {
          bookmarkStatusFetchKeyRef.current = null
          console.warn('[DocumentViewer] Could not load bookmark status for Basecamp todo tab:', err)
          setIsBookmarked(null)
        }
        return
      }

      // For regular documents with Basecamp todo info
      if (!basecampTodoId || !basecampProjectId) {
        setIsBookmarked(null)
        return
      }

      const docFetchKey = `doc:${basecampProjectId}:${basecampTodoId}`
      if (bookmarkStatusFetchKeyRef.current === docFetchKey) return
      bookmarkStatusFetchKeyRef.current = docFetchKey

      try {
        const { basecampApi } = await import('@/lib/basecamp-api')
        const status = await basecampApi.getBookmarkStatus(basecampProjectId.toString(), basecampTodoId.toString())
        setIsBookmarked(status.bookmarked)
      } catch (err) {
        bookmarkStatusFetchKeyRef.current = null
        console.warn('[DocumentViewer] Could not load bookmark status:', err)
        setIsBookmarked(null)
      }
    }

    loadBookmarkStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basecampTodoId, basecampProjectId, activeTabId, location.pathname])

  // Handler for bookmark toggle (for active document)
  const handleToggleBookmark = async () => {
    if (isTogglingBookmark) return

    // Check if we're viewing a Basecamp todo tab
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (currentTab?.type === 'integration-basecamp-todo') {
      // Use the tab bookmark toggle handler instead
      await handleTabToggleBookmark(activeTabId!)
      return
    }

    // For regular documents with Basecamp todo info
    if (!basecampTodoId || !basecampProjectId) return

    try {
      setIsTogglingBookmark(true)
      const { basecampApi } = await import('@/lib/basecamp-api')
      const { toast } = await import('sonner')

      if (isBookmarked) {
        await basecampApi.removeBookmark(basecampProjectId.toString(), basecampTodoId.toString())
        setIsBookmarked(false)
        toast.success('Bookmark removed', {
          description: 'This todo has been removed from your bookmarks'
        })
      } else {
        await basecampApi.addBookmark(basecampProjectId.toString(), basecampTodoId.toString())
        setIsBookmarked(true)
        toast.success('Bookmark added', {
          description: 'This todo has been added to your bookmarks'
        })
      }
    } catch (err) {
      console.error('[DocumentViewer] Error toggling bookmark:', err)
      const { toast } = await import('sonner')
      toast.error('Failed to update bookmark', {
        description: err instanceof Error ? err.message : 'Unknown error'
      })
    } finally {
      setIsTogglingBookmark(false)
    }
  }

  // Handler for tab bookmark toggle (for any tab)
  const handleTabToggleBookmark = async (tabId: string) => {
    try {
      // Check if this is a Basecamp todo tab
      if (tabId.startsWith('basecamp-todo-')) {
        const todoId = tabId.replace('basecamp-todo-', '')
        // Find the tab to get projectId
        const tab = openTabs.find(t => t.id === tabId)
        let projectId = tab?.integrationData?.projectId

        // Fallback: Extract projectId from URL if missing from integrationData
        if (!projectId || projectId === '') {
          const urlMatch = location.pathname.match(BASECAMP_PROJECT_TODO_IN_PATH_RE)
          if (urlMatch && urlMatch[1]) {
            projectId = urlMatch[1]

            // Update tab's integrationData with projectId for future use
            if (tab) {
              setOpenTabs(prevTabs => prevTabs.map(t => {
                if (t.id === tabId && t.integrationData) {
                  return {
                    ...t,
                    integrationData: {
                      ...t.integrationData,
                      projectId: projectId
                    }
                  }
                }
                return t
              }))
            }
          }
        }

        // Get project name from tab title or use default
        const projectName = (tab?.integrationData as any)?.projectName || 'Unknown Project'
        const todoTitle = tab?.title || 'Untitled Todo'

        if (!projectId || projectId === '') {
          const { toast } = await import('sonner')
          toast.error('No project ID found', {
            description: 'Unable to determine Basecamp project for this todo'
          })
          return
        }

        const { basecampApi } = await import('@/lib/basecamp-api')
        const { toast } = await import('sonner')

        // Get current status (for optimistic update)
        let currentStatus: { bookmarked: boolean } | null = null
        try {
          currentStatus = await basecampApi.getBookmarkStatus(projectId.toString(), todoId)
        } catch (err) {
          // If status check fails, assume not bookmarked
          currentStatus = { bookmarked: false }
        }

        const wasBookmarked = currentStatus?.bookmarked || false
        const willBeBookmarked = !wasBookmarked

        // OPTIMISTIC UPDATE: Update UI immediately
        if (activeTabId === tabId) {
          setIsBookmarked(willBeBookmarked)
          setIsTogglingBookmark(true)
        }

        // Optimistically update bookmarks widget
        window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
          detail: {
            todoId,
            projectId: projectId.toString(),
            projectName,
            title: todoTitle,
            added: willBeBookmarked,
            optimistic: true // Signal this is an optimistic update
          }
        }))

        // Make API call in background
        try {
          if (wasBookmarked) {
            await basecampApi.removeBookmark(projectId.toString(), todoId)
          } else {
            await basecampApi.addBookmark(projectId.toString(), todoId)
          }

          // Success - show subtle toast
          toast.success(willBeBookmarked ? 'Bookmarked' : 'Unbookmarked', {
            description: 'Synced with Basecamp',
            duration: 2000,
            position: 'bottom-right'
          })

          // Trigger sync to refresh from server (gets accurate data)
          window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
            detail: {
              todoId,
              projectId: projectId.toString(),
              synced: true // Signal sync is complete
            }
          }))

          // Update local state for active tab
          if (activeTabId === tabId) {
            setIsBookmarked(willBeBookmarked)
            setIsTogglingBookmark(false)
          }
        } catch (apiError) {
          // Rollback on error
          console.error('[DocumentViewer] Error toggling bookmark:', apiError)

          if (activeTabId === tabId) {
            setIsBookmarked(wasBookmarked) // Rollback
            setIsTogglingBookmark(false)
          }

          // Rollback bookmarks widget
          window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
            detail: {
              todoId,
              projectId: projectId.toString(),
              added: wasBookmarked, // Rollback to original state
              optimistic: true
            }
          }))

          toast.error('Failed to sync bookmark', {
            description: apiError instanceof Error ? apiError.message : 'Unknown error',
            duration: 3000
          })
        }

        return
      }

      // Fetch document data for this tab
      let apiUrl: string
      if (tabId.includes('/')) {
        const storagePath = resolveStoragePathForFetch(tabId, workspacesRoute)
        apiUrl = `/api/documents/by-path?path=${encodeURIComponent(storagePath)}`
      } else {
        apiUrl = `/api/documents/${tabId}`
      }

      const response = await fetchWithAuth(apiUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch document')
      }

      const docData = await response.json()
      const frontmatter = docData.frontmatter || {}
      const docPath = docData.filePath || docData.path
      let todoId = resolveLinkageTodoId(docPath, frontmatter)
      let projectId = frontmatter.project?.id || frontmatter.project_id
      if (todoId && !projectId) {
        try {
          const docsRes = await fetchWithAuth(`/api/basecamp/todo-documents/${todoId}?limit=5`)
          if (docsRes.ok) {
            const { documents } = await docsRes.json()
            const first = documents?.[0]
            projectId = first?.frontmatter?.project_id ?? first?.frontmatter?.project?.id
          }
        } catch (_) {}
      }

      if (!todoId || !projectId) {
        const { toast } = await import('sonner')
        toast.error('Not a Basecamp todo', {
          description: 'This document is not linked to a Basecamp todo'
        })
        return
      }

      const { basecampApi } = await import('@/lib/basecamp-api')
      const { toast } = await import('sonner')

      // Check current status
      const status = await basecampApi.getBookmarkStatus(projectId.toString(), todoId.toString())

      if (status.bookmarked) {
        await basecampApi.removeBookmark(projectId.toString(), todoId.toString())
        toast.success('Bookmark removed', {
          description: 'This todo has been removed from your bookmarks'
        })
      } else {
        await basecampApi.addBookmark(projectId.toString(), todoId.toString())
        // No toast for adding - animation in widget will show it
      }

      // Dispatch event to notify bookmarks widget to refresh
      window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
        detail: { todoId: todoId.toString(), projectId: projectId.toString(), added: !status.bookmarked }
      }))
    } catch (err) {
      console.error('[DocumentViewer] Error toggling tab bookmark:', err)
      const { toast } = await import('sonner')
      toast.error('Failed to update bookmark', {
        description: err instanceof Error ? err.message : 'Unknown error'
      })
    }
  }

  // Handler to get tab bookmark status
  const handleGetTabBookmarkStatus = async (tabId: string): Promise<{ isBookmarked: boolean | null; isToggling: boolean }> => {
    try {
      // Check if this is a Basecamp todo tab
      if (tabId.startsWith('basecamp-todo-')) {
        const todoId = tabId.replace('basecamp-todo-', '')
        // Find the tab to get projectId
        const tab = openTabs.find(t => t.id === tabId)
        let projectId = tab?.integrationData?.projectId

        // Fallback: Extract projectId from URL if missing from integrationData
        if (!projectId || projectId === '') {
          const urlMatch = location.pathname.match(BASECAMP_PROJECT_TODO_IN_PATH_RE)
          if (urlMatch && urlMatch[1]) {
            projectId = urlMatch[1]

            // Update tab's integrationData with projectId for future use
            if (tab) {
              setOpenTabs(prevTabs => prevTabs.map(t => {
                if (t.id === tabId && t.integrationData) {
                  return {
                    ...t,
                    integrationData: {
                      ...t.integrationData,
                      projectId: projectId
                    }
                  }
                }
                return t
              }))
            }
          }
        }

        if (!projectId || projectId === '') {
          console.warn('[DocumentViewer] No projectId found for Basecamp todo tab')
          return { isBookmarked: null, isToggling: false }
        }

        const { basecampApi } = await import('@/lib/basecamp-api')
        const status = await basecampApi.getBookmarkStatus(projectId.toString(), todoId)
        return { isBookmarked: status.bookmarked, isToggling: false }
      }

      if (isChatConversationTabId(tabId)) {
        return { isBookmarked: null, isToggling: false }
      }

      // Fetch document data for this tab
      let apiUrl: string
      if (tabId.includes('/')) {
        const storagePath = resolveStoragePathForFetch(tabId, workspacesRoute)
        apiUrl = `/api/documents/by-path?path=${encodeURIComponent(storagePath)}`
      } else {
        apiUrl = `/api/documents/${tabId}`
      }

      const response = await fetchWithAuth(apiUrl)
      if (!response.ok) {
        return { isBookmarked: null, isToggling: false }
      }

      const docData = await response.json()
      const frontmatter = docData.frontmatter || {}
      const docPath = docData.filePath || docData.path
      let todoId = resolveLinkageTodoId(docPath, frontmatter)
      let projectId = frontmatter.project?.id || frontmatter.project_id
      if (todoId && !projectId) {
        try {
          const docsRes = await fetchWithAuth(`/api/basecamp/todo-documents/${todoId}?limit=5`)
          if (docsRes.ok) {
            const { documents } = await docsRes.json()
            const first = documents?.[0]
            projectId = first?.frontmatter?.project_id ?? first?.frontmatter?.project?.id
          }
        } catch (_) {}
      }

      if (!todoId || !projectId) {
        console.warn('[DocumentViewer] Missing todoId or projectId:', { todoId, projectId })
        return { isBookmarked: null, isToggling: false }
      }

      const { basecampApi } = await import('@/lib/basecamp-api')
      const status = await basecampApi.getBookmarkStatus(projectId.toString(), todoId.toString())

      return { isBookmarked: status.bookmarked, isToggling: false }
    } catch (err) {
      console.warn('[DocumentViewer] Could not load tab bookmark status:', err)
      return { isBookmarked: null, isToggling: false }
    }
  }

  // Handlers are now in useDocumentOperations hook - removed old definitions

  // Helper to get current tab slot ID for navigation (using imported utility)
  const getCurrentTabSlotIdHelper = () => getCurrentTabSlotId(activeTabId, openTabs)
  const { configuredIntegrations } = useIntegrations()

  // Helper to check if current view is an integration view (currently unused but may be needed later)
  // const isIntegrationView = viewMode.startsWith("integration-")


  // Initialize navigation for list tab (only if no parent navigation provided)
  useEffect(() => {
    if (!parentNavigation) {
      navigation.navigate("list", { documentId: "list", viewMode: "single" }, "doc-viewer-init-list")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ensure all tabs have tabSlotId values
  useEffect(() => {
    const needsUpdate = openTabs.some(tab => !tab.tabSlotId && tab.id !== 'list')
    if (needsUpdate) {
      const updatedTabs = openTabs.map(tab => {
        if (!tab.tabSlotId && tab.id !== 'list') {
          return { ...tab, tabSlotId: `tab-${tab.id.replace(/[^a-zA-Z0-9]/g, '-')}` }
        }
        return tab
      })
      setOpenTabs(updatedTabs)
    }
  }, [openTabs, setOpenTabs])

  // Auto-cleanup: Validate tabs on mount and remove any dead ones (e.g. after DB rebuild)
  useEffect(() => {
    const validateTabs = async () => {
      // Cold document deep links seed a path-keyed provisional tab before workspaces
      // hydrate. Validating then races restore and toasts "Cleaned up N invalid tab".
      if (isDocumentDeepLinkRestorePending()) {
        return
      }

      const validTabs: OpenTab[] = []
      let removedCount = 0

      for (const tab of openTabs) {
        // Skip special tabs (explorer, home, new documents, integrations, basecamp, graph view)
        if (tab.type === 'explorer' || tab.type === 'home' || tab.type === 'inbox' || tab.type === 'inbox-thread' || tab.type === 'chat-conversation' || tab.type === 'workspace-home' || tab.type === 'edit-history' || tab.type === 'flow-index' || tab.id === 'edit-history' || tab.id === 'view:edit-history' || tab.id === 'graph' || tab.type === 'graph-view' || tab.type === 'search-view' || tab.type === 'person-view' || tab.id.startsWith('new-') || tab.id.startsWith('chat-conversation-') || tab.id.startsWith('integration-') || tab.id.startsWith('basecamp-') || tab.id.startsWith('flow-studio:')) {
          validTabs.push(tab)
          continue
        }

        // Check if document exists
        try {
          const apiUrl = tab.id.includes('/')
            ? `/api/documents/by-path?path=${encodeURIComponent(
                resolveStoragePathForFetch(tab.id, workspacesRoute),
              )}`
            : `/api/documents/${tab.id}`
          const response = await fetchWithRetry(apiUrl, { retryOn404: false, maxRetries: 1 })

          if (response.ok) {
            validTabs.push(tab)
          } else {
            removedCount++
          }
        } catch (err) {
          removedCount++
        }
      }

      if (removedCount > 0) {
        const { toast } = await import('sonner')
        toast.info(`Cleaned up ${removedCount} invalid tab${removedCount > 1 ? 's' : ''}`, {
          description: 'Documents may have been deleted during database rebuild.',
          duration: 4000
        })

        setOpenTabs(validTabs)

        // If active tab was removed, switch to home
        if (!validTabs.some(t => t.id === activeTabId)) {
          setActiveTabId('home')
          if (!isDocumentDeepLinkRestorePending()) {
            navigate('/', { replace: true })
          }
        }
      }
    }

    // Only run on initial mount (not on every tab change)
    if (openTabs.length > 0) {
      validateTabs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount

  // Listen for integration view changes from sidebar
  // Handle graph view open event
  useEffect(() => {
    const handleGraphViewOpen = (event: CustomEvent) => {
      const { openInNewTab } = event.detail || {};
      const graphTabId = 'graph';
      const graphTabTitle = 'Graph View';

      // Use function updates to avoid stale closure values
      setOpenTabs(currentTabs => {
        const existingTab = currentTabs.find(tab => tab.id === graphTabId);
        const currentTabId = activeTabId; // Capture current value

        if (existingTab) {
          // Graph tab already exists, switch to it
          setActiveTabId(graphTabId);
          const tabSlotId = existingTab.tabSlotId || `tab-${graphTabId}`;
          navigation.navigate(tabSlotId, { documentId: graphTabId, viewMode: 'single' }, "graph-view-existing-tab");
          return currentTabs;
        }

        // Create new graph tab
        const newTabSlotId = `tab-${Date.now()}`;

        if (openInNewTab || currentTabId === 'list') {
          // Create new tab
          const newTab: OpenTab = {
            id: graphTabId,
            title: graphTabTitle,
            type: 'graph-view' as const,
            isDirty: false,
            isPinned: false, // Explicitly unpinned by default
            tabSlotId: newTabSlotId,
          };
          setActiveTabId(graphTabId);
          navigation.navigate(newTabSlotId, { documentId: graphTabId, viewMode: 'single' }, "graph-view-new-tab");
          return [...currentTabs, newTab];
        } else {
          // Replace current tab
          const currentTab = currentTabs.find((tab: OpenTab) => tab.id === currentTabId);
          const stableTabSlotId = currentTab?.tabSlotId || newTabSlotId;

          const updatedTabs = currentTabs.map((tab: OpenTab): OpenTab =>
            tab.id === currentTabId
              ? {
                  id: graphTabId,
                  title: graphTabTitle,
                  type: 'graph-view' as const,
                  isDirty: false,
                  tabSlotId: stableTabSlotId,
                }
              : tab
          );
          setActiveTabId(graphTabId);
          navigation.navigate(stableTabSlotId, { documentId: graphTabId, viewMode: 'single' }, "graph-view-replace-current");
          return updatedTabs;
        }
      });
    };

    window.addEventListener('graph-view-open', handleGraphViewOpen as EventListener);
    return () => {
      window.removeEventListener('graph-view-open', handleGraphViewOpen as EventListener);
    };
  }, [navigation]); // Removed activeTabId from deps to prevent re-running on every state change

  // Handle search view open event
  useEffect(() => {
    const handleSearchViewOpen = (event: CustomEvent) => {
      const initialQuery: string = event.detail?.query ?? ''
      const searchPathRaw: string = event.detail?.path ?? ''
      const searchPath = searchPathRaw.trim() || undefined
      const view: string | undefined = event.detail?.view
      const searchTabId = SEARCH_VIEW_TAB_ID
      let slotIdForNav = 'tab-main'
      const targetParams = new URLSearchParams()
      if (view === 'activity') {
        targetParams.set('view', 'activity')
        const user = String(event.detail?.user ?? event.detail?.updatedBy ?? '').trim()
        if (user) targetParams.set('user', user)
      } else {
        if (initialQuery.trim()) targetParams.set('q', initialQuery.trim())
        if (searchPath) targetParams.set('path', searchPath)
      }
      const targetQs = targetParams.toString()
      const targetPath = targetQs ? `/search?${targetQs}` : '/search'

      const namedView: 'search' | 'activity' = view === 'activity' ? 'activity' : 'search'
      const updatedBy = String(event.detail?.user ?? event.detail?.updatedBy ?? '').trim()
      setOpenTabs(currentTabs => {
        const existingTab = currentTabs.find(tab => tab.id === searchTabId)
        if (existingTab) {
          slotIdForNav = existingTab.tabSlotId || 'tab-main'
          return currentTabs.map((tab) =>
            tab.id === searchTabId
              ? {
                  ...tab,
                  initialQuery,
                  searchPath,
                  searchNamedView: namedView,
                  title: namedView === 'activity' ? 'Activity' : 'Search',
                  ...(updatedBy ? { searchUpdatedBy: updatedBy } : {}),
                }
              : tab,
          )
        }
        slotIdForNav = generateTabSlotId()
        const newTab: OpenTab = {
          id: searchTabId,
          title: namedView === 'activity' ? 'Activity' : 'Search',
          type: 'search-view',
          isDirty: false,
          isPinned: false,
          tabSlotId: slotIdForNav,
          initialQuery,
          searchPath,
          searchNamedView: namedView,
          ...(updatedBy ? { searchUpdatedBy: updatedBy } : {}),
        }
        return [...currentTabs, newTab]
      })
      setActiveTabId(searchTabId)
      markProgrammaticNavigation()
      navigate(targetPath, { replace: true })
      navigation.navigate(
        slotIdForNav,
        {
          documentId: searchTabId,
          viewMode: 'single',
          tabType: 'search-view',
          searchQuery: initialQuery,
          searchPath,
        },
        'open-search-view',
      )

      // Cmd+Shift+F / header Search: put caret in the keyword field so typing works
      // without a click. autoFocus only helps on first mount; retries cover lazy load
      // and switching back to an existing Search tab.
      if (namedView === 'search') {
        const focusKeywordInput = () => {
          const el = document.querySelector(
            `[data-testid="${testIds.advancedSearchView.input}"]`,
          ) as HTMLInputElement | null
          if (!el) return false
          el.focus({ preventScroll: true })
          el.select()
          return true
        }
        requestAnimationFrame(() => {
          if (focusKeywordInput()) return
          window.setTimeout(() => {
            if (focusKeywordInput()) return
            window.setTimeout(focusKeywordInput, 100)
          }, 0)
        })
      }
    }

    window.addEventListener('flownotes:open-search-view', handleSearchViewOpen as EventListener)
    return () => {
      window.removeEventListener('flownotes:open-search-view', handleSearchViewOpen as EventListener)
    }
  }, [setOpenTabs, setActiveTabId, navigation, navigate])

  useEffect(() => {
    const handlePersonViewOpen = (event: CustomEvent<OpenPersonViewDetail>) => {
      const userId = String(event.detail?.userId || '').trim()
      if (!userId) return
      const tabId = personViewTabId(userId)
      const title = personViewTabTitle(event.detail?.title || 'Person')
      const current = openTabs.find((tab) => tab.id === activeTabIdRef.current)
      const workspaceId = resolvePersonWorkspaceId(
        event.detail?.workspaceId,
        current?.workspaceId,
        activeWorkspaceIds,
      )
      const slug = workspaceSlugForId(workspaces, workspaceId)
      const existingPerson = openTabs.find(
        (tab) => tab.id === tabId || tab.personUserId === userId,
      )
      const drilling = current?.type === 'person-view'
      const slotIdForNav = drilling
        ? (current?.tabSlotId || 'tab-main')
        : (existingPerson?.tabSlotId || generateTabSlotId())
      if (drilling && current && current.id !== tabId) {
        const tip = navigation.getCurrentEntry(slotIdForNav)
        if (!tip || tip.documentId !== current.id) {
          navigation.navigate(
            slotIdForNav,
            {
              documentId: current.id,
              viewMode: 'single',
              tabType: 'person-view',
              ...(current.title ? { title: current.title } : {}),
              ...(current.workspaceId ? { organizationId: current.workspaceId } : {}),
            },
            'open-person-seed',
          )
        }
      }
      setOpenTabs((currentTabs) => {
        const currentTab = currentTabs.find((tab) => tab.id === activeTabIdRef.current)
        if (currentTab?.type === 'person-view') {
          return drillOpenTabToPerson(currentTabs, currentTab.id, { tabId, userId, title }).map((tab) =>
            tab.id === tabId ? { ...tab, workspaceId, tabSlotId: slotIdForNav } : tab,
          )
        }
        const existingTab = currentTabs.find(
          (tab) => tab.id === tabId || tab.personUserId === userId,
        )
        if (existingTab) {
          return currentTabs.map((tab) =>
            tab.id === existingTab.id
              ? { ...tab, id: tabId, type: 'person-view' as const, personUserId: userId, title, workspaceId, tabSlotId: slotIdForNav }
              : tab,
          )
        }
        const newTab: OpenTab = {
          id: tabId,
          title,
          type: 'person-view',
          isDirty: false,
          isPinned: false,
          tabSlotId: slotIdForNav,
          personUserId: userId,
          workspaceId,
        }
        return [...currentTabs, newTab]
      })
      setActiveTabId(tabId)
      markProgrammaticNavigation()
      navigate(
        slug
          ? `/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}`
          : `/people/${encodeURIComponent(userId)}`,
        { replace: true },
      )
      navigation.navigate(
        slotIdForNav,
        {
          documentId: tabId,
          viewMode: 'single',
          tabType: 'person-view',
          title,
          ...(workspaceId ? { organizationId: workspaceId } : {}),
        },
        'open-person-view',
      )
    }

    window.addEventListener('flownotes:open-person-view', handlePersonViewOpen as EventListener)
    return () => {
      window.removeEventListener('flownotes:open-person-view', handlePersonViewOpen as EventListener)
    }
  }, [setOpenTabs, setActiveTabId, navigation, navigate, openTabs, workspaces, activeWorkspaceIds])

  useEffect(() => {
    const handlePeopleIndexOpen = (event: Event) => {
      const workspaceFromEvent = (event as CustomEvent<{ workspaceId?: string }>).detail?.workspaceId
      const current = openTabs.find((tab) => tab.id === activeTabIdRef.current)
      const workspaceId = resolvePersonWorkspaceId(
        workspaceFromEvent,
        current?.workspaceId,
        activeWorkspaceIds,
      )
      const slug = workspaceSlugForId(workspaces, workspaceId)
      const peopleTab = {
        id: PEOPLE_INDEX_TAB_ID,
        title: 'People',
        type: 'person-view' as const,
        isDirty: false,
        isPinned: false,
        tabSlotId: current?.tabSlotId || generateTabSlotId(),
        workspaceId,
      }
      const placed = openPeopleIndexInTabs(openTabs, current?.id ?? activeTabIdRef.current, peopleTab)
      if (placed.replacedTabId && current) {
        const tip = navigation.getCurrentEntry(placed.slotId)
        if (!tip || tip.documentId !== current.id) {
          navigation.navigate(
            placed.slotId,
            navigationFieldsFromOpenTab(current),
            'open-people-index-seed',
          )
        }
      }
      setOpenTabs(placed.tabs)
      setActiveTabId(PEOPLE_INDEX_TAB_ID)
      markProgrammaticNavigation()
      navigate(slug ? `/${encodeURIComponent(slug)}/people` : '/people', { replace: true })
      navigation.navigate(
        placed.slotId,
        {
          documentId: PEOPLE_INDEX_TAB_ID,
          viewMode: 'single',
          tabType: 'person-view',
          title: 'People',
          ...(workspaceId ? { organizationId: workspaceId } : {}),
        },
        'open-people-index',
      )
    }

    window.addEventListener('flownotes:open-people-index', handlePeopleIndexOpen)
    return () => {
      window.removeEventListener('flownotes:open-people-index', handlePeopleIndexOpen)
    }
  }, [setOpenTabs, setActiveTabId, navigation, navigate, openTabs, workspaces, activeWorkspaceIds])

  // Ref keeps the latest handler without re-registering the listener on every render.
  // Without this, StrictMode + navigation state changes (tabNavigationState updates on every
  // navigate() call) cause the useEffect to re-run mid-dispatch, adding a second listener that
  // fires for the same event — resulting in every navigation being recorded twice.
  //
  // IMPORTANT: navigation.navigate() must NOT be called inside setOpenTabs functional updaters.
  // React StrictMode invokes functional updaters twice (to detect impure updaters), which would
  // cause navigation.navigate() to fire twice per event → duplicate history entries.
  const integrationViewChangeHandlerRef = useRef<((event: CustomEvent) => void) | null>(null);
  integrationViewChangeHandlerRef.current = (event: CustomEvent) => {
      const { tabId, title, type, integrationData, openInNewTab, workspaceId: detailWorkspaceId } =
        event.detail || {};

      // For Basecamp todos, title can be empty initially (will be loaded by viewer)
      const isBasecampTodo = type === 'integration-basecamp-todo';
      const isBasecampTriage = type === 'integration-basecamp-triage';

      // Validation: tabId and type are required, title is required unless it's a Basecamp todo or triage
      if (!tabId || !type || (!title && !isBasecampTodo && !isBasecampTriage)) {
        console.error('[Integration View Change] Invalid event detail:', event.detail);
        return;
      }

      // Use placeholder title for Basecamp todos if not provided; triage has fixed title
      const finalTitle = title || (isBasecampTodo ? 'Loading...' : isBasecampTriage ? 'Weekly Triage' : '');

      // Compute everything from the current render snapshot (handler is updated each render via ref).
      // Using openTabs directly (not functional updater) avoids calling navigate() inside setState,
      // which StrictMode would invoke twice.
      const existingTab = openTabs.find(tab => tab.id === tabId);
      // Sidebar passes org on integrationData.organizationId; other callers may set detail.workspaceId.
      const ws = resolveWorkspaceIdForNewIntegrationTab(
        openTabs,
        detailWorkspaceId ?? integrationData?.organizationId
      )

      if (openInNewTab) {
        // CMD+click: Always create new tab
        markTabClickNavigation();
        const newTabSlotId = `tab-${Date.now()}`;
        const newTab: OpenTab = {
          id: tabId,
          title: finalTitle,
          type: type,
          isDirty: false,
          tabSlotId: newTabSlotId,
          integrationData: integrationData,
          ...(ws != null && { workspaceId: ws }),
        };
        setOpenTabs(prev => [...prev, newTab]);
        setActiveTabId(tabId);
        navigation.navigate(
          newTabSlotId,
          navigationFieldsFromIntegrationOpen({ tabId, type, integrationData }),
          "integration-view-cmd-new-tab",
        );
      } else if (existingTab) {
        // Normal click + already open: Switch to existing tab, updating integrationData if provided
        markTabClickNavigation();
        setActiveTabId(tabId);
        const tabSlotId = existingTab.tabSlotId || `tab-${tabId}`;
        console.log('[NavRecord] Existing tab:', tabId, 'slotId:', tabSlotId);
        navigation.navigate(
          tabSlotId,
          navigationFieldsFromIntegrationOpen({ tabId, type, integrationData: integrationData ?? existingTab.integrationData }),
          "integration-view-existing-tab",
        );
        // Refresh integrationData in case it changed (e.g. section was added after restore from localStorage)
        if (integrationData || ws != null) {
          setOpenTabs(prev => prev.map((tab: OpenTab): OpenTab =>
            tab.id === tabId
              ? {
                  ...tab,
                  ...(integrationData ? { integrationData } : {}),
                  ...(ws != null ? { workspaceId: ws } : {}),
                }
              : tab
          ));
        }
      } else {
        // Normal click + not open: Replace current active tab if unpinned
        const currentActiveTabId = activeTabIdRef.current;
        const currentTab = currentActiveTabId ? openTabs.find(tab => tab.id === currentActiveTabId) : null;

        if (openTabs.length === 0 || !currentActiveTabId || currentActiveTabId === 'list' || currentTab?.isPinned) {
          // No tabs, list view, or pinned tab: Create new tab
          markTabClickNavigation();
          const newTabSlotId = `tab-${Date.now()}`;
          const newTab: OpenTab = {
            id: tabId,
            title: finalTitle,
            type: type,
            isDirty: false,
            isPinned: false,
            tabSlotId: newTabSlotId,
            integrationData: integrationData,
            ...(ws != null && { workspaceId: ws }),
          };
          setOpenTabs(prev => [...prev, newTab]);
          setActiveTabId(tabId);
          console.log('[NavRecord] New tab (no existing):', tabId, 'slotId:', newTabSlotId);
          navigation.navigate(
            newTabSlotId,
            navigationFieldsFromIntegrationOpen({ tabId, type, integrationData }),
            "integration-view-new-tab-no-existing",
          );
        } else {
          // Replace current unpinned tab — seed prior id when slot history is empty
          // or tip does not match the tab being replaced (Back must stay usable).
          markTabClickNavigation();
          const stableTabSlotId = currentTab?.tabSlotId || `tab-${Date.now()}`;
          const tip = navigation.getCurrentEntry(stableTabSlotId)
          if (
            currentTab &&
            currentActiveTabId !== tabId &&
            (!tip || tip.documentId !== currentActiveTabId)
          ) {
            navigation.navigate(
              stableTabSlotId,
              navigationFieldsFromOpenTab(currentTab),
              "integration-seed-previous",
            )
          }
          setOpenTabs(prev => prev.map((tab: OpenTab): OpenTab =>
            tab.id === currentActiveTabId
              ? {
                  id: tabId,
                  title: finalTitle,
                  type: type,
                  isDirty: false,
                  tabSlotId: stableTabSlotId,
                  integrationData: integrationData,
                  ...(ws != null && { workspaceId: ws }),
                }
              : tab
          ));
          setActiveTabId(tabId);
          console.log('[NavRecord] Replacing tab', currentActiveTabId, '→', tabId, 'slotId:', stableTabSlotId);
          navigation.navigate(
            stableTabSlotId,
            navigationFieldsFromIntegrationOpen({ tabId, type, integrationData }),
            "integration-view-replace-current",
          );
        }
      }
  };

  useEffect(() => {
    const stableHandler = (event: Event) => {
      integrationViewChangeHandlerRef.current?.(event as CustomEvent);
    };
    window.addEventListener('integration-view-change', stableHandler);
    return () => {
      window.removeEventListener('integration-view-change', stableHandler);
    };
  }, []); // Register once — handler body stays fresh via ref above

  useEffect(() => {
    const enrichHandler = (event: Event) => {
      const detail = (event as CustomEvent<{
        tabId: string
        breadcrumbTrail?: OpenTab['integrationData'] extends infer T ? T extends { breadcrumbTrail?: infer B } ? B : never : never
        title?: string
        listName?: string
        projectName?: string
      }>).detail
      if (!detail?.tabId) return

      setOpenTabs((prev) =>
        prev.map((tab): OpenTab => {
          if (tab.id !== detail.tabId) return tab
          const projectIdFromTab =
            tab.integrationData?.projectId ||
            tab.id.match(/^basecamp-project-(.+)$/)?.[1] ||
            undefined
          return {
            ...tab,
            title: detail.title ?? tab.title,
            integrationData: {
              ...tab.integrationData,
              integrationId: tab.integrationData?.integrationId ?? 'basecamp',
              ...(projectIdFromTab && !tab.integrationData?.projectId
                ? { projectId: projectIdFromTab }
                : {}),
              breadcrumbTrail: detail.breadcrumbTrail ?? tab.integrationData?.breadcrumbTrail,
              listName: detail.listName ?? tab.integrationData?.listName,
              projectName: detail.projectName ?? tab.integrationData?.projectName,
            },
          }
        })
      )
    }
    window.addEventListener('integration-tab-enrich', enrichHandler)
    return () => window.removeEventListener('integration-tab-enrich', enrichHandler)
  }, [setOpenTabs])

  // Update URL after the active tab changes. Ignore location-only updates: a link
  // click changes the bar before the tab switches, and this effect would replace
  // that destination with the still-active inbox thread.
  const urlSyncTabIdRef = useRef(activeTabId)
  useEffect(() => {
    const tabChanged = urlSyncTabIdRef.current !== activeTabId
    urlSyncTabIdRef.current = activeTabId
    if (!tabChanged) return

    // Skip for Explorer view
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!activeTabId || currentTab?.type === 'explorer') {
      return
    }

    // Find the active tab
    const activeTab = openTabs.find(tab => tab.id === activeTabId)
    if (!activeTab) {
      return
    }

    const targetUrl = buildAppUrl({ tab: activeTab, workspaces: workspacesRoute })

    // A share link is still restoring. This effect closes over the previous tab
    // (People) for a frame and would replace /doc/{uuid} with that tab's URL.
    if (isDocumentDeepLinkRestorePending()) {
      return
    }

    // Only update if URL is different
    if (location.pathname !== targetUrl) {
      navigate(targetUrl, { replace: true })
      // Do NOT clearTabClickNavigation here - clearing after 100ms races URL→tab
      // sync and can spawn a second Basecamp tab with empty history (Back disabled).
      // markTabClickNavigation()'s own timeout clears the flag.
    }
  }, [activeTabId, openTabs, navigate, location.pathname, workspaces])

  // Keyboard shortcuts for navigation (only if no parent handlers provided)
  // Note: This hook must be called before any early returns, but handlers are defined later
  // Use refs to avoid dependency issues
  const handleGoBackRef = useRef<(() => Promise<void>) | undefined>(undefined)
  const handleGoForwardRef = useRef<(() => Promise<void>) | undefined>(undefined)

  const navigationShortcuts = useMemo(() => {
    if (!parentGoBack && !parentGoForward) {
      return [
        {
          key: "[",
          modKey: true,
          handler: () => {
            if (handleGoBackRef.current) {
              handleGoBackRef.current()
            }
          },
          description: "Go back",
        },
        {
          key: "]",
          modKey: true,
          handler: () => {
            if (handleGoForwardRef.current) {
              handleGoForwardRef.current()
            }
          },
          description: "Go forward",
        },
      ]
    }
    return []
  }, [parentGoBack, parentGoForward])

  useKeyboardShortcuts(navigationShortcuts)

  // Cmd/Ctrl+S save only. Do NOT bind Cmd/Ctrl+Shift+R - that is Chrome's hard reload
  // (stolen here since 2026-07-17 via capture-phase preventDefault). Doc refresh stays on the toolbar.
  const handleManualSaveRef = useRef(handleManualSave)
  const rendererSaveableRef = useRef(true)
  useEffect(() => {
    handleManualSaveRef.current = handleManualSave
  }, [handleManualSave])

  const documentEditShortcuts = useMemo(
    () => [
      {
        key: 's',
        modKey: true,
        shiftKey: false,
        handler: () => {
          if (!rendererSaveableRef.current) return
          if (shouldSkipExternalDocumentPolling(activeTabId, activeTabForPolling)) return
          handleManualSaveRef.current()
          toast.success('Saving…', { id: 'doc-manual-save', duration: 1200 })
        },
        description: 'Save document',
      },
    ],
    [activeTabId, activeTabForPolling]
  )
  useKeyboardShortcuts(documentEditShortcuts)

  // ESC key to exit editor focus and focus sidebar - handled in DocumentContent

  const handleInternalLinkClick = async (href: string, event?: MouseEvent) => {
    try {
      // Get current document info to pass file path for relative resolution
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      const currentFilePath = currentTab?.filePath || (currentTab?.id?.includes('/') ? currentTab.id : undefined)

      const hrefToResolve = hrefForInAppDocumentNavigation(href) ?? href
      const resolvedLink = await resolveDocumentLink(hrefToResolve, currentFilePath)

      if (resolvedLink) {
        // Use filePath instead of UUID for better tab management
        // The parent's handleDocumentClick works with both UUIDs and file paths
        const targetId = resolvedLink.filePath || resolvedLink.documentId

        // Use parent's handleDocumentClick if provided (from resizable-layout)
        // This properly integrates with the tab system helpers
        if (parentDocumentClick && event) {
          // Create a synthetic React.MouseEvent to pass modifier keys
          const syntheticEvent = {
            metaKey: event.metaKey,
            ctrlKey: event.ctrlKey,
            preventDefault: () => {},
            stopPropagation: () => {}
          } as React.MouseEvent
          await parentDocumentClick(targetId, syntheticEvent)
        } else if (parentDocumentClick) {
          await parentDocumentClick(targetId)
        } else {
          // Fallback to local implementation
          await handleDocumentClick(targetId, event?.metaKey || event?.ctrlKey)
        }
      } else {
        console.warn(`[Link Resolution] Document not found for link: ${href}`)
        toast.error('Document not found', {
          description: `No cloud doc for ${href}. It may not be synced yet.`,
          duration: 4000,
        })
      }
    } catch (error) {
      console.error('[Link Resolution] Failed to resolve link:', error)
      toast.error('Could not open link', {
        description: href,
        duration: 4000,
      })
    }
  }

  // handleDocumentClick is now defined earlier (before hook call) - removed duplicate
  // Tab management handlers (handleTabClick, handleTabClose, handleTabReorder, handleTabPinToggle, handleTabDragToChat) are now from useTabManagement hook

  const handleTitleEdit = () => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (currentTab && activeTabId !== "list" && activeTabId !== "home" && currentTab.type !== 'home') {
      skipTitleBlurSaveRef.current = false
      setEditedTitle(currentTab.title)
      setEditingTitle(true)
      // Focus input after render
      setTimeout(() => titleInputRef.current?.focus(), 0)
    }
  }

  const cancelTitleEdit = useCallback(() => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    skipTitleBlurSaveRef.current = true
    setEditedTitle(currentTab?.title || '')
    setEditingTitle(false)
  }, [openTabs, activeTabId])

  const handleTitleSave = async () => {
    if (skipTitleBlurSaveRef.current) {
      skipTitleBlurSaveRef.current = false
      return
    }
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!editedTitle.trim() || currentTab?.type === "explorer") {
      setEditingTitle(false)
      return
    }

    try {
      const {
        updateDocument,
        renameDocument,
        generateFilenameFromTitle,
        isAutoSlugUntitledFilename,
      } = await import('../../lib/api')

      const effectivePath =
        currentDocumentPath ||
        currentTab?.filePath ||
        (activeTabId.includes('/') ? activeTabId : null)

      // Update the title in DB. Do NOT pass writeToFile here — if a rename follows, the
      // rename route handles the filesystem move. Passing writeToFile risks writing empty
      // DB body content to disk before the editor's unsaved paste has autosaved (data loss).
      await updateDocument(activeTabId, { title: editedTitle.trim() }, { pathFallback: effectivePath })

      // While filename is still provisional (untitled-note.md / untitled-note-N.md), keep file basename in sync with title.
      if (effectivePath) {
        const filename = effectivePath.split('/').pop() || ''
        const shouldAutoSlug = isAutoSlugUntitledFilename(filename)

        if (shouldAutoSlug) {
          try {
            const newFilename = generateFilenameFromTitle(editedTitle.trim())
            if (newFilename.toLowerCase() === filename.toLowerCase()) {
              const updatedTabs = openTabs.map(tab =>
                tab.id === activeTabId ? { ...tab, title: editedTitle.trim() } : tab
              )
              setOpenTabs(updatedTabs)
            } else {
              const renameResult = await renameDocument(activeTabId, newFilename, {
                pathFallback: effectivePath,
              })

              if (renameResult.success && renameResult.document.newPath) {
                const newTabKey = renameResult.document.id || renameResult.document.newPath
                const updatedTabs = openTabs.map(tab =>
                  tab.id === activeTabId
                    ? {
                        ...tab,
                        id: newTabKey,
                        filePath: renameResult.document.newPath,
                        title: editedTitle.trim(),
                      }
                    : tab
                )
                setOpenTabs(updatedTabs)
                setActiveTabId(newTabKey)
                setCurrentDocumentPath(renameResult.document.newPath)

                markProgrammaticNavigation()
                navigate(documentUrlForPathOrId(renameResult.document.newPath, workspacesRoute))

                window.dispatchEvent(new CustomEvent('document-renamed', {
                  detail: {
                    oldPath: renameResult.document.oldPath,
                    newPath: renameResult.document.newPath
                  }
                }))

                const { toast } = await import('sonner')
                toast.success('Document renamed', {
                  description: `${filename} → ${newFilename}`
                })
              } else {
                const updatedTabs = openTabs.map(tab =>
                  tab.id === activeTabId ? { ...tab, title: editedTitle.trim() } : tab
                )
                setOpenTabs(updatedTabs)
              }
            }
          } catch (renameError) {
            console.error('Failed to rename file:', renameError)
            const msg = renameError instanceof Error ? renameError.message : String(renameError)
            const { toast } = await import('sonner')
            if (/already exists|409/i.test(msg)) {
              toast.error('That file name is taken', {
                description: 'Another note in this folder already uses this title as its file name. Try a different title.',
              })
            } else {
              toast.warning('Title saved', {
                description: 'The file could not be renamed automatically.',
              })
            }
            const updatedTabs = openTabs.map(tab =>
              tab.id === activeTabId ? { ...tab, title: editedTitle.trim() } : tab
            )
            setOpenTabs(updatedTabs)
          }
        } else {
          // Not an untitled file, just update the title
          const updatedTabs = openTabs.map(tab =>
            tab.id === activeTabId ? { ...tab, title: editedTitle.trim() } : tab
          )
          setOpenTabs(updatedTabs)
        }
      } else {
        // No current document path, just update the title
        const updatedTabs = openTabs.map(tab =>
          tab.id === activeTabId ? { ...tab, title: editedTitle.trim() } : tab
        )
        setOpenTabs(updatedTabs)
      }

      setEditingTitle(false)
    } catch (error) {
      console.error('Failed to update title:', error)
      // Could show a toast notification here
      setEditingTitle(false)
    }
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void handleTitleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      cancelTitleEdit()
    }
  }


  const handleGoBack = useCallback(async () => {
    // Use unified navigation history - delegate to parent handler if available
    // Parent handler (in resizable-layout) handles both Explorer and document navigation
    if (parentGoBack) {
      return parentGoBack()
    }

    // Fallback: use unified history directly
    const entry = navigation.goBack('tab-main')
    if (entry) {
      if (entry.folderPath !== undefined) {
        // Navigate to Explorer folder - delegate to parent
        if (onFolderClick) {
          onFolderClick(entry.folderPath || '')
        }
      } else if (entry.documentId) {
        // Navigate to document
        markTabClickNavigation()
        const existingTab = openTabs.find(tab => tab.id === entry.documentId)
        if (existingTab) {
          setActiveTabId(entry.documentId)
          setViewMode(entry.viewMode)
        } else if (parentDocumentClick) {
          await parentDocumentClick(entry.documentId)
        }
      }
    }
  }, [parentGoBack, navigation, onFolderClick, openTabs, setActiveTabId, setViewMode, parentDocumentClick])

  const handleGoForward = useCallback(async () => {
    // Use unified navigation history - delegate to parent handler if available
    // Parent handler (in resizable-layout) handles both Explorer and document navigation
    if (parentGoForward) {
      return parentGoForward()
    }

    // Fallback: use unified history directly
    const entry = navigation.goForward('tab-main')
    if (entry) {
      if (entry.folderPath !== undefined) {
        // Navigate to Explorer folder - delegate to parent
        if (onFolderClick) {
          onFolderClick(entry.folderPath || '')
        }
      } else if (entry.documentId) {
        // Navigate to document
        markTabClickNavigation()
        const existingTab = openTabs.find(tab => tab.id === entry.documentId)
        if (existingTab) {
          setActiveTabId(entry.documentId)
          setViewMode(entry.viewMode)
        } else if (parentDocumentClick) {
          await parentDocumentClick(entry.documentId)
        }
      }
    }
  }, [parentGoForward, navigation, onFolderClick, openTabs, setActiveTabId, setViewMode, parentDocumentClick])

  // Update refs so keyboard shortcuts can call handlers
  useEffect(() => {
    handleGoBackRef.current = handleGoBack
    handleGoForwardRef.current = handleGoForward
  }, [handleGoBack, handleGoForward])

  useEffect(() => {
    const tab = openTabs.find((item) => item.id === activeTabId)
    if (tab?.type !== 'integration-nextcloud') {
      setNextcloudShellNav(false, false)
      return
    }
    const slot = tab.tabSlotId || 'tab-main'
    setNextcloudShellNav(navigation.canGoBack(slot), navigation.canGoForward(slot))
  }, [openTabs, activeTabId, navigation])

  // Tab reorder and drag to chat handlers are now from useTabManagement hook (lines 271-276)

  const resolveDefaultCreateFolderPath = useCallback((): {
    folderPath: string
    workspaceId: string | undefined
  } => {
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    const wsContentRoot = currentTab?.workspaceId
      ? workspaces.find((w) => w.id === currentTab.workspaceId)?.contentRoot
      : undefined
    const fallbackWorkspace = activeWorkspaceIds
      .map((id) => workspaces.find((w) => w.id === id))
      .find((w) => w?.contentRoot?.trim())
    const deskWorkspace =
      (currentTab?.workspaceId
        ? workspaces.find((w) => w.id === currentTab.workspaceId)
        : null) ?? fallbackWorkspace
    const deskFolderPath = deskWorkspace
      ? deskPathForMember(deskWorkspace, deskSlug)
      : ''
    const scopedWorkspaceContentRoots = (
      currentTab?.workspaceId
        ? [workspaces.find((w) => w.id === currentTab.workspaceId)?.contentRoot]
        : activeWorkspaceIds.map((id) => workspaces.find((w) => w.id === id)?.contentRoot)
    )
    const folderPath = resolveCreateFolderPath({
      activeTab: currentTab,
      selectedProjectId,
      workspaceContentRoot: wsContentRoot,
      fallbackWorkspaceContentRoot: fallbackWorkspace?.contentRoot,
      deskFolderPath: deskFolderPath || null,
      scopedWorkspaceContentRoots,
    })
    const workspaceId =
      currentTab?.workspaceId ??
      (folderPath
        ? resolveWorkspaceForDocument(folderPath, workspaces)
        : fallbackWorkspace?.id)
    return { folderPath, workspaceId }
  }, [
    openTabs,
    activeTabId,
    workspaces,
    activeWorkspaceIds,
    selectedProjectId,
    deskSlug,
  ])

  const newDocumentFolderLabel = useMemo(() => {
    const { folderPath, workspaceId } = resolveDefaultCreateFolderPath()
    const ws = workspaceId
      ? workspaces.find((w) => w.id === workspaceId)
      : undefined
    const fallback =
      (ws?.slug && String(ws.slug).trim()) ||
      (ws?.name && String(ws.name).trim()) ||
      'workspace'
    return folderLabelForCreatePath(folderPath, fallback)
  }, [resolveDefaultCreateFolderPath, workspaces])

  const createUntitledNoteInFolder = useCallback(async (folderPathOverride?: string) => {
    try {
      const defaults = resolveDefaultCreateFolderPath()
      const folderPath =
        typeof folderPathOverride === 'string' ? folderPathOverride : defaults.folderPath
      const workspaceId =
        (folderPath
          ? resolveWorkspaceForDocument(folderPath, workspaces)
          : undefined) ?? defaults.workspaceId

      const { createDocument } = await import('../../lib/api')
      const result = await createDocument({
        title: 'Untitled Note',
        content: '',
        document_type: 'note',
        folder_path: folderPath,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      })

      if (!result.document) {
        throw new Error('Document creation failed - no document returned')
      }

      markDocumentForEditOnOpen(result.document.id)
      if (result.document.filePath) {
        markDocumentForEditOnOpen(result.document.filePath)
      }

      // Open in new tab with real document ID
      const newTabSlotId = `tab-${Date.now()}`
      // Convert API document type to OpenTab type (API uses snake_case, OpenTab uses kebab-case)
      const apiType = result.document.type
      const tabType: OpenTab['type'] = apiType === 'work_spec' ? 'work-spec' : (apiType === 'voice_memo' ? 'voice-memo' : (apiType as OpenTab['type'] || 'note'))

      const tabDocKey = result.document.id
      const docFilePath = result.document.filePath ?? null
      const tabWorkspaceId =
        workspaceId ??
        (docFilePath ? resolveWorkspaceForDocument(docFilePath, workspaces) : undefined)
      const newTab: OpenTab = {
        id: tabDocKey,
        title: result.document.title,
        type: tabType,
        isDirty: false,
        isPinned: false,
        tabSlotId: newTabSlotId,
        filePath: docFilePath,
        ...(tabWorkspaceId != null && { workspaceId: tabWorkspaceId }),
      }

      setOpenTabs([...openTabs, newTab])
      setActiveTabId(tabDocKey)
      setViewMode("single")
      navigation.navigate(newTabSlotId, { documentId: tabDocKey, viewMode: "single" }, "doc-viewer-create-tab-from-result")

      markProgrammaticNavigation()
      navigate(
        documentUrlForPathOrId(result.document.filePath || result.document.id, workspacesRoute),
        { replace: true },
      )

      if (docFilePath) {
        window.dispatchEvent(
          new CustomEvent('show-document-in-browse', {
            detail: { filePath: docFilePath, documentId: tabDocKey, forceRefresh: true },
          }),
        )
      }

      // Refresh file tree to show new document
      window.dispatchEvent(new CustomEvent('document-created', {
        detail: {
          path: result.document.filePath || tabDocKey,
          folderPath: folderPath
        }
      }))
    } catch (error) {
      console.error('Failed to create document:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to create document', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [
    resolveDefaultCreateFolderPath,
    workspaces,
    openTabs,
    setOpenTabs,
    setActiveTabId,
    setViewMode,
    navigation,
    markProgrammaticNavigation,
    navigate,
    workspacesRoute,
  ])

  const handleNewDocument = useCallback(async () => {
    await createUntitledNoteInFolder()
  }, [createUntitledNoteInFolder])

  const handleChooseNewDocumentFolder = useCallback(() => {
    setCreateFolderPickerOpen(true)
  }, [])

  const handleImportInDefaultFolder = useCallback(() => {
    const { folderPath, workspaceId } = resolveDefaultCreateFolderPath()
    dispatchCreateInFolder('import', { folderPath, workspaceId })
  }, [resolveDefaultCreateFolderPath])

  const handleNewFolderInDefaultFolder = useCallback(() => {
    const { folderPath } = resolveDefaultCreateFolderPath()
    dispatchCreateInFolder('folder', { folderPath })
  }, [resolveDefaultCreateFolderPath])

  const handleCreateDocumentInFolder = useCallback(
    (folderPath: string) => {
      setCreateFolderPickerOpen(false)
      void createUntitledNoteInFolder(folderPath)
    },
    [createUntitledNoteInFolder],
  )

  const handleAIDocumentSave = (_content: string, type: DocumentType, title: string) => {
    // Update the current document with generated content
    const updatedTabs = openTabs.map(tab =>
      tab.id === activeTabId
        ? { ...tab, title, type, isDirty: true }
        : tab
    )
    setOpenTabs(updatedTabs)

    // Switch to normal document view
    setActiveTabId(activeTabId)
  }

  const handleAIDocumentCancel = () => {
    // Close the new document tab
    const updatedTabs = openTabs.filter(tab => tab.id !== activeTabId)
    setOpenTabs(updatedTabs)
    setActiveTabId(updatedTabs.length > 0 ? updatedTabs[0].id : "list")
  }

  const handleSendToChat = (text: string, documentId: string) => {
    // Close the new document tab and send to chat
    const updatedTabs = openTabs.filter(tab => tab.id !== activeTabId)
    setOpenTabs(updatedTabs)
    setActiveTabId(updatedTabs.length > 0 ? updatedTabs[0].id : "list")

    // Send to chat via parent component
    onSendToChat?.(text, documentId)
  }

  // State for document frontmatter and source URL (not in hook)
  const [documentFrontmatter, setDocumentFrontmatter] = useState<Record<string, any> | null>(null)
  const [documentLifecycleStatus, setDocumentLifecycleStatus] = useState<string | null>(null)
  const [documentOriginalPath, setDocumentOriginalPath] = useState<string | null>(null)
  const [documentBodyForChangelog, setDocumentBodyForChangelog] = useState('')
  const [documentSourceUrl, setDocumentSourceUrl] = useState<string | null>(null)
  const [documentDbId, setDocumentDbId] = useState<string | null>(null)
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false)
  const [ttsConfigured, setTtsConfigured] = useState(false)
  const [generatedAudioDocId, setGeneratedAudioDocId] = useState<string | null>(null)
  const [watchLiveAgentId, setWatchLiveAgentId] = useState<string | null>(null)
  const [isArchivingOrRestoring, setIsArchivingOrRestoring] = useState(false)
  const activeRendererKind = useMemo(() => {
    const tab = openTabs.find((candidate) => candidate.id === activeTabId)
    return resolveDocumentRendererKind({
      kind: String(documentFrontmatter?.kind || ""),
      type: String(documentFrontmatter?.type || tab?.type || "note"),
      filePath: currentDocumentPath || tab?.filePath || "",
    })
  }, [
    activeTabId,
    currentDocumentPath,
    documentFrontmatter?.kind,
    documentFrontmatter?.type,
    openTabs,
  ])
  const activeRendererCapabilities = getRendererCapabilities(activeRendererKind)
  useEffect(() => {
    rendererSaveableRef.current = activeRendererCapabilities.saveable
  }, [activeRendererCapabilities.saveable])
  const handleOriginalDocumentDownload = useCallback(async () => {
    const documentId =
      documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    if (!documentId) {
      toast.error("Document is not ready to download")
      return
    }
    const filename =
      (currentDocumentPath || openTabs.find((tab) => tab.id === activeTabId)?.filePath || "")
        .replace(/\\/g, "/")
        .split("/")
        .filter(Boolean)
        .pop() || "image"
    try {
      await downloadImageDocumentFile(documentId, filename)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Download failed")
    }
  }, [activeTabId, currentDocumentPath, documentDbId, openTabs])

  const isArchivedDoc = useMemo(
    () =>
      isArchivedDocument({
        status: documentLifecycleStatus,
        frontmatter: documentFrontmatter,
        filePath: currentDocumentPath,
      }),
    [documentLifecycleStatus, documentFrontmatter, currentDocumentPath],
  )

  const [publishPickerOpen, setPublishPickerOpen] = useState(false)
  const [isPublishingToDrive, setIsPublishingToDrive] = useState(false)
  const [isDisconnectingFromDrive, setIsDisconnectingFromDrive] = useState(false)

  // Push to Google Drive state (force overwrite escape hatch)
  const [isPushingToDrive, setIsPushingToDrive] = useState(false)
  const [pushToDriveSuccess, setPushToDriveSuccess] = useState(false)

  // Pull from Google Drive state (force overwrite escape hatch)
  const [isPullingFromDrive, setIsPullingFromDrive] = useState(false)
  const [pullFromDriveSuccess, setPullFromDriveSuccess] = useState(false)
  const [gdocSaveGeneration, setGdocSaveGeneration] = useState(0)
  const prevSavingRef = useRef(false)

  const tabForCloudDispatch = openTabs.find((tab) => tab.id === activeTabId)
  const cloudDispatchDocumentId =
    documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)

  // True when the current document is a dispatch-ready bet (visible to cloud agent)
  const isBetDispatchReady = (() => {
    const fm = documentFrontmatter
    if (!fm) return false
    if (fm.type !== 'bet') return false
    const status = fm.status ?? ''
    if (['complete', 'shaping', 'shaped'].includes(status)) return false
    if (fm.safe_to_dispatch === false) return false
    if (fm.dispatch_target && fm.dispatch_target !== 'cursor-cloud') return false
    return true
  })()

  const shouldFetchCloudDispatch = Boolean(
    documentFrontmatter?.dispatched_agent_id &&
      String(documentFrontmatter.dispatched_agent_id).trim()
  )

  const {
    dispatch: cloudDispatch,
    isDispatching: isDispatchingToCloud,
    isActiveDispatch: isCloudDispatchRunning,
    dispatchToCloud,
  } = useCloudDispatchForDocument({
    documentId: cloudDispatchDocumentId,
    filePath: currentDocumentPath || tabForCloudDispatch?.filePath || null,
    organizationId: tabForCloudDispatch?.workspaceId ?? null,
    enabled: shouldFetchCloudDispatch,
  })

  const handleDispatchToCloudFromViewer = async () => {
    const result = await dispatchToCloud('flownotes')
    if (result.success) {
      toast.success('Dispatched to Cloud Agent', {
        description: documentFrontmatter?.title || tabForCloudDispatch?.title,
      })
    } else {
      toast.error('Dispatch failed', { description: result.error || 'Unknown error' })
    }
  }

  // Load headings whenever the active document changes (pill shows always, TOC on demand)
  useEffect(() => {
    // Only fetch for real document UUIDs — skip list views, special tabs, integrations
    const isRealDoc = activeTabId &&
      !activeTabId.startsWith('basecamp') &&
      activeTabId !== 'graph' &&
      activeTabId !== 'list' &&
      activeTabId !== 'home' &&
      activeTabId !== 'inbox' &&
      /^[0-9a-f-]{36}$/i.test(activeTabId)
    if (!isRealDoc) {
      setTocHeadings([])
      return
    }
    let cancelled = false
    fetchWithAuth(`/api/documents/${activeTabId}`)
      .then((res) => res.ok ? res.json() : null)
      .catch(() => null)
      .then((data) => {
        if (cancelled || !data?.content) return
        const headings = extractHeadings(data.content)
        setTocHeadings(headings)
        // Compute ratios after next paint (needs scrollEl to be populated)
        const tryCompute = (attempts = 0) => {
          if (cancelled) return
          const contentEl = tocContentRef.current
          const scrollTarget = tocContentRef.current?.querySelector<HTMLElement>('[data-scroll-container], .tiptap-editor, .cm-scroller')
          if (contentEl && scrollTarget) {
            setTocHeadings((prev) => computeHeadingRatios(prev, contentEl, scrollTarget))
          } else if (attempts < 10) {
            setTimeout(() => tryCompute(attempts + 1), 100)
          }
        }
        requestAnimationFrame(() => tryCompute())
      })
    return () => { cancelled = true }
  }, [activeTabId])

  // Reset headings on tab change
  useEffect(() => {
    setTocOpen(false)
    setTocHeadings([])
    setScrollEl(null)
    setDocumentDbId(null)
  }, [activeTabId])

  // After each tab change, find the actual scrolling element inside DocumentContent
  // (TipTap's .tiptap-editor or CodeMirror's .cm-scroller) and store in scrollEl state.
  // Using state (not just ref) so components that depend on scrollEl re-register effects.
  useEffect(() => {
    if (!activeTabId) return
    let cancelled = false
    // Retry briefly in case DocumentContent is still mounting
    const tryFind = (attempts = 0) => {
      if (cancelled) return
      const el = tocContentRef.current?.querySelector<HTMLElement>('[data-scroll-container], .tiptap-editor, .cm-scroller')
      if (el) {
        setScrollEl(el)
      } else if (attempts < 10) {
        setTimeout(() => tryFind(attempts + 1), 100)
      }
    }
    setTimeout(() => tryFind(), 50)
    return () => { cancelled = true }
  }, [activeTabId, viewMode])
  // Selected item in the folder explorer — drives the resizable preview panel
  const [selectedExplorerItem, setSelectedExplorerItem] = useState<SelectedItem | null>(null)
  // Whether the explorer preview pane is pinned open (user-controlled toggle)
  const [explorerPreviewOpen, setExplorerPreviewOpen] = useState(false)

  // Listen for 'Preview' context menu action — open preview pane with the selected item
  useEffect(() => {
    const handler = (e: CustomEvent<{ type: 'document' | 'folder'; id: string; path: string; name: string }>) => {
      const { type, id, path, name } = e.detail
      if (type === 'document') {
        setSelectedExplorerItem({ kind: 'document', doc: { id, title: name, filePath: path } as any })
      } else {
        setSelectedExplorerItem({ kind: 'folder', path, name })
      }
      setExplorerPreviewOpen(true)
    }
    window.addEventListener('explorer-preview-item', handler as EventListener)
    return () => window.removeEventListener('explorer-preview-item', handler as EventListener)
  }, [])
  // documentSaveState and undoState are now from useDocumentOperations hook
  // handleManualSave and handleUndo are now from useDocumentOperations hook

  // Removed duplicate handlers - they're now in useDocumentOperations hook:
  // All handlers (handleRenameClick, handleRenameConfirm, handleDeleteClick, handleDelete,
  // handleAddToChatClick, handleExportPDF, handleManualSave, handleUndo) are now from useDocumentOperations hook
  // History checking useEffect is also in the hook
  // Tab context menu event listeners are also in the hook

  // Removed duplicate handleDelete - it's now in useDocumentOperations hook

  // Info pane animation and close handler are now in useViewMode hook

  // Shared with the folder toolbar's CursorWorkspaceMenu and Basecamp todo header - see
  // frontend/src/hooks/use-open-in-cursor.ts for the generate-workspace + cursor:// launch flow.
  // This viewer calls it per-tab (varying todoId/projectName/basecampProjectId), so pass those
  // as per-call overrides rather than binding them once.
  const { handleOpenInCursor: sharedHandleOpenInCursor } = useOpenInCursor({
    onProjectNotLinked: (info) => {
      setWorkspaceLinkContext({ projectName: info.projectName || '', basecampProjectId: info.basecampProjectId })
      setWorkspaceLinkDialogOpen(true)
    },
  })

  const handleOpenInCursor = useCallback(async (todoId: string, pName: string, bcProjectId?: string | null, docId?: string | null) => {
    const slug = pName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    await sharedHandleOpenInCursor({
      todoId,
      projectName: pName,
      projectSlug: slug,
      basecampProjectId: bcProjectId || undefined,
      documentId: docId || undefined,
    })
  }, [sharedHandleOpenInCursor])

  // Context menu for main panel folder view — dispatch so left-sidebar shows same ContextMenu
  const handleExplorerFolderContextMenu = useCallback((folder: { name: string; path: string }, event: React.MouseEvent) => {
    window.dispatchEvent(new CustomEvent('open-context-menu', {
      detail: {
        x: event.clientX,
        y: event.clientY,
        context: { type: 'folder' as const, value: folder.name, path: folder.path },
      },
    }))
  }, [])
  const handlePanelSearchChange = useCallback((q: string) => {
    setOpenTabs((prev) => {
      let changed = false
      const next = prev.map((tab) => {
        if (tab.id !== activeTabId || tab.type !== 'explorer') return tab
        const current = tab.explorerData?.panelSearchQuery || ''
        if (current === q) return tab
        changed = true
        return {
          ...tab,
          explorerData: {
            folderPath: tab.explorerData?.folderPath ?? null,
            panelSearchQuery: q || undefined,
          },
        }
      })
      return changed ? next : prev
    })
  }, [activeTabId, setOpenTabs])

  const handleExplorerDocumentContextMenu = useCallback((doc: { id: string; title: string; tags?: string[] }, event: React.MouseEvent) => {
    const filePath = (doc as { filePath?: string; path?: string }).filePath ?? (doc as { filePath?: string; path?: string }).path
    window.dispatchEvent(new CustomEvent('open-context-menu', {
      detail: {
        x: event.clientX,
        y: event.clientY,
        context: {
          type: 'document' as const,
          value: doc.title,
          documentId: doc.id,
          filePath: filePath ?? doc.id,
          path: filePath ?? doc.id,
          tags: doc.tags,
        },
      },
    }))
  }, [])

  const handlePushToDrive = useCallback(async () => {
    const docId = documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    if (!docId) return
    const pushOrgId = tabForCloudDispatch?.workspaceId ?? activeWorkspaceIds[0] ?? null
    setIsPushingToDrive(true)
    try {
      const res = await fetchWithAuth('/api/google-drive/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: docId, organizationId: pushOrgId }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        toast.error('Push to Drive failed', { description: body?.error || `HTTP ${res.status}` })
        return
      }
      setPushToDriveSuccess(true)
      toast.success('Pushed to Drive')
      setDocumentFrontmatter((prev) =>
        prev
          ? { ...prev, gdoc_last_pushed: new Date().toISOString() }
          : prev,
      )
      setTimeout(() => setPushToDriveSuccess(false), 2000)
    } catch (err) {
      toast.error('Push to Drive failed', { description: String(err) })
    } finally {
      setIsPushingToDrive(false)
    }
  }, [documentDbId, activeTabId, tabForCloudDispatch, activeWorkspaceIds])

  const handlePullFromDrive = useCallback(async () => {
    const gdocId = documentFrontmatter?.gdoc_id
    if (!gdocId || typeof gdocId !== 'string' || isPullingFromDrive) return
    const docId = documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    if (!docId) return
    const pullOrgId = tabForCloudDispatch?.workspaceId ?? activeWorkspaceIds[0] ?? null
    setIsPullingFromDrive(true)
    try {
      const res = await fetchWithAuth('/api/google-drive/pull', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: docId, organizationId: pullOrgId }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        toast.error('Pull from Drive failed', { description: (body as {error?: string})?.error ?? `HTTP ${res.status}` })
        return
      }
      setPullFromDriveSuccess(true)
      toast.success('Pulled latest from Google Drive', { description: 'Document updated with Drive content.' })
      await externalChangeDetection.refreshDocument()
      setTimeout(() => setPullFromDriveSuccess(false), 2000)
    } catch (err) {
      toast.error('Pull from Drive failed', { description: String(err) })
    } finally {
      setIsPullingFromDrive(false)
    }
  }, [documentFrontmatter, isPullingFromDrive, documentDbId, activeTabId, tabForCloudDispatch, activeWorkspaceIds, externalChangeDetection])

  const handleGenerateAudio = useCallback(async () => {
    const docId = documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    if (!docId || isGeneratingAudio || !ttsConfigured) return
    setIsGeneratingAudio(true)
    try {
      await generateDocumentTts(docId)
      setGeneratedAudioDocId(docId)
      toast.success('Lesson audio ready')
      await externalChangeDetection.refreshDocument()
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: docId } }))
    } catch (err) {
      toast.error('Could not generate audio', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setIsGeneratingAudio(false)
    }
  }, [activeTabId, documentDbId, externalChangeDetection, isGeneratingAudio, ttsConfigured])

  useEffect(() => {
    const docId = documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    const isAudioDoc =
      String(documentFrontmatter?.type || '') === 'audio' ||
      /\.mp3$/i.test(currentDocumentPath || '')
    if (!docId || isAudioDoc || isArchivedDoc) {
      setTtsConfigured(false)
      return
    }
    let cancelled = false
    void fetchDocumentTtsStatus(docId)
      .then((status) => {
        if (!cancelled) setTtsConfigured(status.configured === true)
      })
      .catch(() => {
        if (!cancelled) setTtsConfigured(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeTabId, currentDocumentPath, documentDbId, documentFrontmatter?.type, isArchivedDoc])

  const gdocLinkedId =
    typeof documentFrontmatter?.gdoc_id === 'string' && documentFrontmatter.gdoc_id.trim() !== ''
      ? documentFrontmatter.gdoc_id.trim()
      : null

  const publishOrgId = tabForCloudDispatch?.workspaceId ?? activeWorkspaceIds[0] ?? null
  const { authStatus: driveAuthStatus } = useGoogleDriveAuth(publishOrgId ?? undefined)
  const driveConnected = driveAuthStatus?.connected === true
  const showGoogleDriveMenu = Boolean(gdocLinkedId) || driveConnected

  const gdocSync = useGdocSync({
    gdocId: gdocLinkedId,
    documentId: cloudDispatchDocumentId,
    organizationId: publishOrgId,
    frontmatter: documentFrontmatter,
    setFrontmatter: setDocumentFrontmatter,
    refreshDocument: () => externalChangeDetection.refreshDocument(),
    saveGeneration: gdocSaveGeneration,
  })

  const openPublishPicker = useCallback(async () => {
    if (!publishOrgId) {
      toast.error('Cannot publish', {
        description: 'Select a workspace with Google Drive connected.',
      })
      return
    }
    if (!driveConnected) {
      toast.error('Google Drive not connected', {
        description: 'Connect Google Drive in workspace settings first.',
      })
      return
    }
    const currentTab = openTabs.find((t) => t.id === activeTabId)
    if (currentTab?.isDirty) {
      const saveFirst = window.confirm(
        'This document has unsaved changes. Save before publishing to Drive?',
      )
      if (!saveFirst) return
      try {
        await handleManualSave()
      } catch (err) {
        toast.error('Save failed', { description: String(err) })
        return
      }
    }
    setPublishPickerOpen(true)
  }, [publishOrgId, driveConnected, openTabs, activeTabId, handleManualSave])

  const handlePublishToDrive = useCallback(
    async (parentFolderId: string, folderLabel: string) => {
      const docId =
        documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
      if (!docId) {
        toast.error('Cannot publish', { description: 'Document id not available.' })
        return
      }
      setIsPublishingToDrive(true)
      try {
        const result = await publishDocumentToGoogleDrive({
          documentId: docId,
          parentFolderId,
          organizationId: publishOrgId,
        })
        if (result.frontmatter) {
          setDocumentFrontmatter((prev) => ({
            ...(prev || {}),
            ...result.frontmatter,
          }))
        } else {
          setDocumentFrontmatter((prev) => ({
            ...(prev || {}),
            gdoc_id: result.fileId,
            gdoc_last_pushed: result.pushed,
            gdoc_sync_mode: 'manual',
          }))
        }
        setPublishPickerOpen(false)
        toast.success('Published to Drive', {
          description: `Created in ${folderLabel}`,
          action: result.webViewLink
            ? {
                label: 'Open in Drive',
                onClick: () =>
                  window.open(result.webViewLink, '_blank', 'noopener,noreferrer'),
              }
            : undefined,
        })
        await externalChangeDetection.refreshDocument()
      } catch (err) {
        const hint =
          err && typeof err === 'object' && 'hint' in err
            ? String((err as { hint?: string }).hint || '')
            : ''
        const status =
          err && typeof err === 'object' && 'status' in err
            ? Number((err as { status?: number }).status)
            : 0
        toast.error(
          status === 409 ? 'Already linked to Drive' : 'Publish to Drive failed',
          {
            description:
              hint ||
              (err instanceof Error ? err.message : String(err)) ||
              (status === 409 ? 'Use Sync instead of Publish.' : undefined),
          },
        )
      } finally {
        setIsPublishingToDrive(false)
      }
    },
    [
      documentDbId,
      activeTabId,
      publishOrgId,
      setDocumentFrontmatter,
      externalChangeDetection,
    ],
  )

  const performDisconnectFromDrive = useCallback(async () => {
    const docId =
      documentDbId || (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
    if (!docId || !gdocLinkedId) return
    setIsDisconnectingFromDrive(true)
    try {
      const result = await unlinkDocumentFromGoogleDrive({
        documentId: docId,
        organizationId: publishOrgId,
      })
      setDocumentFrontmatter(result.frontmatter || {})
      toast.success('Disconnected from Drive', {
        description: 'Link removed. Google Doc was not deleted.',
        action: result.webViewLink
          ? {
              label: 'Open Doc',
              onClick: () =>
                window.open(result.webViewLink, '_blank', 'noopener,noreferrer'),
            }
          : undefined,
      })
      await externalChangeDetection.refreshDocument()
    } catch (err) {
      toast.error('Disconnect failed', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setIsDisconnectingFromDrive(false)
    }
  }, [
    documentDbId,
    activeTabId,
    gdocLinkedId,
    publishOrgId,
    setDocumentFrontmatter,
    externalChangeDetection,
  ])

  const handleDisconnectFromDrive = useCallback(() => {
    if (!gdocLinkedId || isDisconnectingFromDrive) return
    const confirmToast = toast.info('Disconnect this document from Google Drive?', {
      description: 'Maater will stop syncing. The Google Doc stays in Drive unchanged.',
      duration: 10000,
      action: {
        label: 'Disconnect',
        onClick: () => {
          toast.dismiss(confirmToast)
          void performDisconnectFromDrive()
        },
      },
      cancel: {
        label: 'Cancel',
        onClick: () => toast.dismiss(confirmToast),
      },
    })
  }, [gdocLinkedId, isDisconnectingFromDrive, performDisconnectFromDrive])

  /** Maater reload; when Drive-linked also run Sync (manual + Keep synced). */
  const handleExternalRefresh = useCallback(async () => {
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (currentTab?.type === 'integration-product-board') {
      window.dispatchEvent(new CustomEvent('product-board-refresh'))
      return
    }
    if (currentTab?.type === 'integration-basecamp') {
      const section = resolveBasecampIndexSection(currentTab) || 'todos'
      window.dispatchEvent(new CustomEvent('basecamp-section-refresh', { detail: { section } }))
      return
    }

    const hasUnsavedChanges = currentTab?.isDirty || false
    const alsoSyncDrive = Boolean(gdocLinkedId)

    const runRefreshAndMaybeSync = async () => {
      await externalChangeDetection.refreshDocument()
      if (alsoSyncDrive) {
        await gdocSync.runSync({ interactive: true })
      }
    }

    if (hasUnsavedChanges) {
      toast.warning('Cannot refresh with unsaved changes', {
        description: alsoSyncDrive
          ? 'Save your changes first, then refresh and sync with Drive'
          : 'Save your changes first, then refresh',
        action: {
          label: alsoSyncDrive ? 'Save, refresh & sync' : 'Save & Refresh',
          onClick: async () => {
            await handleManualSave()
            await runRefreshAndMaybeSync()
          },
        },
        duration: 8000,
      })
      return
    }

    await runRefreshAndMaybeSync()
  }, [
    openTabs,
    activeTabId,
    gdocLinkedId,
    externalChangeDetection,
    gdocSync.runSync,
    handleManualSave,
  ])

  // After a save completes, bump generation so Keep synced can push
  useEffect(() => {
    const saving = Boolean(documentSaveState?.saving)
    if (prevSavingRef.current && !saving) {
      setGdocSaveGeneration((n) => n + 1)
    }
    prevSavingRef.current = saving
  }, [documentSaveState?.saving])

  const navigateToFolderAfterArchive = useCallback(
    (folderPath: string) => {
      // Use the shared folder-open path (explorer tab + programmatic URL guard).
      // Do not hand-roll tab/URL updates here - a parallel navigate used to race URL
      // sync into document probes (todos.md / README 404 storms on leaf folders).
      if (onFolderClick) {
        onFolderClick(folderPath)
        return
      }
      markProgrammaticNavigation()
      const currentTabSlot =
        openTabs.find((tab) => tab.id === activeTabId)?.tabSlotId || 'tab-main'
      const wsId = getWorkspaceIdForPath?.(folderPath)
      const explorerTab: OpenTab = {
        id: 'list',
        title: 'Explorer',
        type: 'explorer',
        isDirty: false,
        isPinned: false,
        tabSlotId: currentTabSlot,
        explorerData: { folderPath: folderPath || null },
        ...(wsId != null ? { workspaceId: wsId } : {}),
      }
      setOpenTabs(
        openTabs.map((tab) => (tab.id === activeTabId ? explorerTab : tab)),
      )
      setActiveTabId('list')
      setSelectedProjectId?.(folderPath || null)
      setShowDocumentList?.(true)
      navigation.navigate(
        currentTabSlot,
        { documentId: null, folderPath: folderPath || null, viewMode: 'list' },
        'archive-show-parent-folder',
      )
      navigate(
        folderPath ? documentUrlForPathOrId(folderPath, workspacesRoute) : '/',
        { replace: true },
      )
    },
    [
      openTabs,
      activeTabId,
      getWorkspaceIdForPath,
      setOpenTabs,
      setActiveTabId,
      setSelectedProjectId,
      setShowDocumentList,
      navigation,
      workspacesRoute,
      navigate,
      onFolderClick,
    ],
  )

  const handleArchiveDocument = useCallback(async () => {
    if (!activeTabId || activeTabId.startsWith('new-') || isArchivingOrRestoring) return
    setIsArchivingOrRestoring(true)
    try {
      const uuid = await resolveDocumentRowId(
        activeTabId,
        currentDocumentPath,
        { workspaces: workspacesRoute },
      )
      const response = await fetchWithAuth(`/api/documents/${uuid}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        toast.error(err.error || 'Failed to archive document')
        return
      }
      const data = await response.json().catch(() => ({}))
      const originalPath =
        data?.document?.original_path ||
        data?.document?.originalPath ||
        currentDocumentPath
      const archivedPath =
        data?.document?.file_path ||
        data?.document?.filePath ||
        null
      const archivedDocId = data?.document?.id || uuid
      const parentFolder =
        parentFolderFromContentPath(originalPath) ||
        parentFolderFromDocumentPath(originalPath) ||
        ''
      // Open via `open-document` (live layout handler), not handleDocumentClick from this
      // closure - after navigateToFolderAfterArchive the toast still holds a stale
      // activeTabId/openTabs snapshot, so replaceCurrentTab was a no-op and orphan
      // recovery bounced back to the folder explorer.
      const openDocumentFromToast = (idOrPath: string, openInNewTab = true) => {
        window.dispatchEvent(
          new CustomEvent('open-document', {
            detail: { filePath: idOrPath, openInNewTab },
          }),
        )
      }

      toast.success('Document archived', {
        description: parentFolder
          ? `Moved to archive. Showing ${formatUserFacingStoragePath(parentFolder, workspacesRoute)}.`
          : 'Moved to archive.',
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const undoRes = await fetchWithAuth(
                `/api/documents/${archivedDocId}/unarchive`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                },
              )
              if (!undoRes.ok) {
                const err = await undoRes.json().catch(() => ({}))
                toast.error(err.error || 'Failed to undo archive')
                return
              }
              const undoData = await undoRes.json().catch(() => ({}))
              const restoredPath =
                undoData?.document?.file_path ||
                undoData?.document?.filePath ||
                originalPath
              toast.success('Archive undone', {
                description: restoredPath
                  ? `Restored to ${formatUserFacingStoragePath(restoredPath, workspacesRoute)}`
                  : 'Document restored to its previous location.',
              })
              openDocumentFromToast(archivedDocId)
            } catch (undoErr) {
              toast.error(
                undoErr instanceof Error ? undoErr.message : 'Failed to undo archive',
              )
            }
          },
        },
        cancel: {
          label: 'View',
          onClick: () => {
            openDocumentFromToast(archivedDocId || archivedPath || '')
          },
        },
      })
      navigateToFolderAfterArchive(parentFolder)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive document')
    } finally {
      setIsArchivingOrRestoring(false)
    }
  }, [
    activeTabId,
    isArchivingOrRestoring,
    currentDocumentPath,
    workspacesRoute,
    navigateToFolderAfterArchive,
  ])

  const handleUnarchiveDocument = useCallback(async () => {
    if (!activeTabId || activeTabId.startsWith('new-') || isArchivingOrRestoring) return
    setIsArchivingOrRestoring(true)
    try {
      const uuid = await resolveDocumentRowId(
        activeTabId,
        currentDocumentPath,
        { workspaces: workspacesRoute },
      )
      const response = await fetchWithAuth(`/api/documents/${uuid}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        toast.error(err.error || 'Failed to unarchive document')
        return
      }
      const data = await response.json().catch(() => ({}))
      const restoredPath =
        data?.document?.file_path ||
        data?.document?.filePath ||
        documentOriginalPath ||
        resolveOriginalPathBeforeArchive({
          originalPath: documentOriginalPath,
          filePath: currentDocumentPath,
        })
      toast.success('Document restored')
      setDocumentLifecycleStatus(data?.document?.status || 'completed')
      setDocumentOriginalPath(null)
      setDocumentFrontmatter((prev) =>
        prev
          ? { ...prev, status: 'completed', original_path: undefined, archived_at: undefined }
          : { status: 'completed' },
      )
      if (restoredPath) {
        setCurrentDocumentPath(restoredPath)
        setOpenTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeTabId
              ? {
                  ...tab,
                  filePath: restoredPath,
                  ...(isUuid(tab.id) ? {} : { id: restoredPath }),
                }
              : tab,
          ),
        )
        markProgrammaticNavigation()
        const targetUrl = documentUrlForPathOrId(restoredPath, workspacesRoute)
        navigate(targetUrl, { replace: true })
        window.dispatchEvent(
          new CustomEvent('show-folder-in-browse', {
            detail: {
              folderPath: parentFolderFromContentPath(restoredPath) || '',
            },
          }),
        )
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unarchive document')
    } finally {
      setIsArchivingOrRestoring(false)
    }
  }, [
    activeTabId,
    isArchivingOrRestoring,
    currentDocumentPath,
    workspacesRoute,
    documentOriginalPath,
    setCurrentDocumentPath,
    setOpenTabs,
    navigate,
  ])

  // Stable callback for DocumentContent memo - only updates when documentId matches active tab
  const handleFrontmatterChange = useCallback((frontmatter: Record<string, any> | null, documentId?: string) => {
    if (documentId !== undefined && documentId !== activeTabIdRef.current) return
    setDocumentFrontmatter(frontmatter)
    let sourceUrl = frontmatter?.url || frontmatter?.source_url || null
    const todoId = resolveLinkageTodoId(currentDocumentPath ?? undefined, frontmatter)
    const projectId = frontmatter?.project?.id || frontmatter?.project_id
    if (!sourceUrl && todoId && projectId) {
      const recordType = detectBasecampRecordType(todoId, {
        frontmatter,
        filePath: currentDocumentPath ?? undefined,
      })
      sourceUrl = resolveBasecampAppUrl({ recordId: todoId, projectId, recordType })
    } else if (sourceUrl && todoId && projectId) {
      const recordType = detectBasecampRecordType(todoId, {
        frontmatter,
        filePath: currentDocumentPath ?? undefined,
      })
      sourceUrl = resolveBasecampAppUrl({
        recordId: todoId,
        projectId,
        recordType,
        candidateUrl: sourceUrl,
      })
    } else if (!sourceUrl && frontmatter?.todo_id && projectId) {
      sourceUrl = resolveBasecampAppUrl({
        recordId: frontmatter.todo_id,
        projectId,
        recordType: detectBasecampRecordType(frontmatter.todo_id, { frontmatter }),
      })
    }
    setDocumentSourceUrl(sourceUrl)
  }, [currentDocumentPath])

  // Keep the tab's title/filePath (the source of truth for the header - see `currentTitle` below)
  // in sync with whatever DocumentContent actually loaded. Renames performed via a chat tool call
  // update the document in the DB/file immediately, but previously only a manual UI rename wrote
  // the new title back into `openTabs`, so the header showed a stale title until reload. See
  // docs/product/incidents/2026-07-08-document-rename-editor-desync.md
  const handleDocumentMetaSync = useCallback((documentId: string, meta: { title?: string; filePath?: string | null }) => {
    setOpenTabs(prevTabs => {
      const tab = prevTabs.find(t => t.id === documentId)
      if (!tab) return prevTabs
      const nextTitle = meta.title && meta.title !== tab.title ? meta.title : undefined
      const nextFilePath = meta.filePath && meta.filePath !== tab.filePath ? meta.filePath : undefined
      if (nextTitle === undefined && nextFilePath === undefined) return prevTabs
      return prevTabs.map(t =>
        t.id === documentId
          ? { ...t, ...(nextTitle !== undefined ? { title: nextTitle } : {}), ...(nextFilePath !== undefined ? { filePath: nextFilePath } : {}) }
          : t
      )
    })
  }, [setOpenTabs])


  const siblingTextImportFolder = useMemo(() => {
    const tab = openTabs.find((t) => t.id === activeTabId)
    if (!tab) return null
    if (
      tab.type === "home" ||
      tab.type === "inbox" ||
      tab.type === "inbox-thread" ||
      tab.type === "chat-conversation" ||
      tab.type === "explorer" ||
      tab.type === "workspace-home" ||
      tab.type === "edit-history" ||
      tab.type === "graph-view" ||
      tab.type === "search-view" ||
      tab.type === "person-view" ||
      tab.type?.startsWith("integration-") ||
      tab.id === "graph" ||
      tab.id === "edit-history" ||
      tab.id.startsWith("new-")
    ) {
      return null
    }
    const path =
      typeof tab.filePath === "string"
        ? tab.filePath
        : typeof tab.id === "string" && tab.id.includes("/")
          ? tab.id
          : null
    if (!path || isMarkdownProductBoardFilePath(path)) return null
    return parentFolderFromDocumentPath(path)
  }, [openTabs, activeTabId])

  const [textImportDropActive, setTextImportDropActive] = useState(false)
  /** Where a tab-bar file drop will place the new import relative to the active tab. */
  const [textImportTabPlacement, setTextImportTabPlacement] = useState<'left' | 'right' | 'replace'>('right')
  const textImportPlacementRef = useRef<'left' | 'right' | 'replace'>('right')
  /** Nested dragenter/leave counter - relatedTarget checks alone miss cancelled drops. */
  const textImportDragDepthRef = useRef(0)

  const clearTextImportDropState = useCallback(() => {
    textImportDragDepthRef.current = 0
    setTextImportDropActive(false)
    setTextImportTabPlacement('right')
    textImportPlacementRef.current = 'right'
  }, [])

  useEffect(() => {
    clearTextImportDropState()
  }, [activeTabId, siblingTextImportFolder, clearTextImportDropState])

  // Safety net: cancel / drop outside still clears sticky overlay (same pattern as chat-pane).
  useEffect(() => {
    const onDragEnd = () => clearTextImportDropState()
    const onWindowDrop = () => clearTextImportDropState()
    window.addEventListener('dragend', onDragEnd)
    window.addEventListener('drop', onWindowDrop)
    return () => {
      window.removeEventListener('dragend', onDragEnd)
      window.removeEventListener('drop', onWindowDrop)
    }
  }, [clearTextImportDropState])

  // After import opens a new tab, slide it left/right of the drop anchor tab.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        documentId?: string
        relativeToTabId?: string | null
        placement?: 'left' | 'right' | 'replace'
      }>).detail
      const documentId = detail?.documentId
      const placement = detail?.placement ?? 'right'
      const relativeToTabId = detail?.relativeToTabId
      if (!documentId || placement === 'replace') return

      setOpenTabs((prev) => {
        const tab = prev.find((t) => t.id === documentId)
        if (!tab) return prev
        const without = prev.filter((t) => t.id !== documentId)
        const relIdx = relativeToTabId
          ? without.findIndex((t) => t.id === relativeToTabId)
          : -1
        if (relIdx < 0) return [...without, tab]
        const insertAt = placement === 'left' ? relIdx : relIdx + 1
        return [...without.slice(0, insertAt), tab, ...without.slice(insertAt)]
      })
      setActiveTabId(documentId)
    }
    window.addEventListener('place-open-tab-relative', handler as EventListener)
    return () => window.removeEventListener('place-open-tab-relative', handler as EventListener)
  }, [setOpenTabs, setActiveTabId])

  const canAcceptSiblingTextImport = siblingTextImportFolder != null

  const updateTextImportPlacementFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const tabBar = document.querySelector(
        `[data-testid="${testIds.documentViewer.tabList}"]`,
      )
      const overTabBar =
        tabBar instanceof HTMLElement &&
        (() => {
          const r = tabBar.getBoundingClientRect()
          return (
            clientX >= r.left &&
            clientX <= r.right &&
            clientY >= r.top &&
            clientY <= r.bottom
          )
        })()

      // Dropping on the document body defaults to "new tab on the right".
      if (!overTabBar || !activeTabId) {
        if (textImportPlacementRef.current !== 'right') {
          textImportPlacementRef.current = 'right'
          setTextImportTabPlacement('right')
        }
        return
      }

      const activeTabEl = document.querySelector(
        `[data-tab-id="${CSS.escape(activeTabId)}"]`,
      )
      if (!(activeTabEl instanceof HTMLElement)) {
        if (textImportPlacementRef.current !== 'right') {
          textImportPlacementRef.current = 'right'
          setTextImportTabPlacement('right')
        }
        return
      }

      const rect = activeTabEl.getBoundingClientRect()
      let next: 'left' | 'right' | 'replace'
      if (clientX < rect.left) {
        next = 'left'
      } else if (clientX > rect.right) {
        next = 'right'
      } else {
        const rel = (clientX - rect.left) / Math.max(rect.width, 1)
        if (rel < 0.33) next = 'left'
        else if (rel > 0.67) next = 'right'
        else next = 'replace'
      }

      if (textImportPlacementRef.current !== next) {
        textImportPlacementRef.current = next
        setTextImportTabPlacement(next)
      }
    },
    [activeTabId],
  )

  const siblingTextImportFolderLabel = useMemo(
    () =>
      formatUserFacingStoragePath(
        siblingTextImportFolder,
        workspaces.map((w) => ({
          id: w.id,
          slug: w.slug,
          name: w.name,
          contentRoot: w.contentRoot,
        })),
      ),
    [siblingTextImportFolder, workspaces],
  )

  const textImportDropHeadline =
    textImportTabPlacement === 'left'
      ? 'Drop to insert tab on the left'
      : textImportTabPlacement === 'replace'
        ? 'Drop to replace this tab'
        : 'Drop to open as a new tab on the right'

  // Show Explorer view when Explorer tab is active
  const currentTab = openTabs.find(tab => tab.id === activeTabId)

  // --- Soft bookmark eligibility (must run before explorer early return - Rules of Hooks) ---
  // Reading-margin commit had put bookmark hooks below this return; folder-hub URLs
  // (e.g. /flownotes-product/deployment) then hit React #300 "fewer hooks than expected".
  const _tabTypeForBookmark = currentTab?.type as string | undefined
  const softBookmarkDocId =
    !!currentTab &&
    _tabTypeForBookmark !== 'integration-basecamp-todo' &&
    _tabTypeForBookmark !== 'integration-basecamp-triage' &&
    _tabTypeForBookmark !== 'integration-basecamp' &&
    _tabTypeForBookmark !== 'integration-product-board' &&
    _tabTypeForBookmark !== 'explorer' &&
    _tabTypeForBookmark !== 'home' &&
    !String(activeTabId ?? '').startsWith('new-')
      ? (activeTabId ?? null)
      : null

  if (currentTab?.type === "explorer") {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-background">
        {/* Dedicated Tab Row */}
        <div
          className="print-hide"
          style={{
            backgroundColor: 'hsl(var(--tab-bar-bg))',
            borderColor: 'hsl(var(--tab-bar-border))',
          }}
        >
          <DocumentTabs
            openTabs={openTabs}
            activeTabId={tabBarActiveId ?? activeTabId}
            onTabClick={handleTabClick}
            onTabClose={handleTabClose}
            onTabReorder={handleTabReorder}
            onTabDragToChat={handleTabDragToChat}
            onNativeSidebarItemDrop={onNativeSidebarExplorerDropOnTabBar}
            onTabPinToggle={handleTabPinToggle}
            onNewTab={handleNewDocument}
            onImportInFolder={handleImportInDefaultFolder}
            onNewFolder={handleNewFolderInDefaultFolder}
            onChooseNewDocumentFolder={handleChooseNewDocumentFolder}
            newDocumentFolderLabel={newDocumentFolderLabel}
          />
        </div>

        {/* Document List Content */}
        <div className="flex-1 relative overflow-hidden">
          <DocumentList
            onDocumentClick={handleDocumentClick}
            onNewDocument={handleNewDocument}
            projectFilter={currentTab.explorerData?.folderPath ?? null}
            activeDocumentId={activeDocumentIdForFolderView}
            onFolderClick={onFolderClick}
            workspaceColor={workspaceColor}
            onGoBack={parentGoBack}
            onGoForward={parentGoForward}
            canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
            canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
            selectedItem={selectedExplorerItem}
            onItemSelect={(item) => {
              setSelectedExplorerItem(item)
            }}
            onFolderContextMenu={handleExplorerFolderContextMenu}
            onDocumentContextMenu={handleExplorerDocumentContextMenu}
            previewPaneOpen={explorerPreviewOpen}
            onTogglePreviewPane={() => setExplorerPreviewOpen(v => !v)}
            workspaces={workspacesRoute}
            onPanelSearchChange={handlePanelSearchChange}
            preview={
              explorerPreviewOpen && selectedExplorerItem ? (
                <DocumentPreviewPane
                  item={selectedExplorerItem}
                  workspaceColor={workspaceColor}
                  onOpen={(event) => {
                    if (selectedExplorerItem.kind === 'folder') {
                      onFolderClick?.(selectedExplorerItem.path)
                      window.dispatchEvent(new CustomEvent('show-folder-in-browse', { detail: { folderPath: selectedExplorerItem.path } }))
                    } else {
                      handleDocumentClick(selectedExplorerItem.doc.id, isModClick(event))
                    }
                    setSelectedExplorerItem(null)
                  }}
                  onSelectChild={(child) => {
                    if (selectedExplorerItem.kind !== 'folder') return
                    onFolderClick?.(selectedExplorerItem.path)
                    window.dispatchEvent(new CustomEvent('show-folder-in-browse', {
                      detail: { folderPath: selectedExplorerItem.path },
                    }))
                    setSelectedExplorerItem(child)
                  }}
                  onClose={() => { setSelectedExplorerItem(null); setExplorerPreviewOpen(false) }}
                />
              ) : null
            }
          />
        </div>
      </div>
    )
  }

  // Find active tab for use in content area and info pane
  const activeTab = openTabs.find(tab => tab.id === activeTabId);
  const isFlowDocumentForToc =
    currentTab?.type === 'flow' ||
    documentFrontmatter?.type === 'flow' ||
    (typeof currentTab?.filePath === 'string' && currentTab.filePath.includes('/flows/')) ||
    (typeof currentTab?.id === 'string' && currentTab.id.includes('/flows/'));
  // Quiz LMS has its own chrome (Preview/Quiz/Results/Runs); the floating TOC pill
  // overlaps the quiz title bar and is not useful for fence-based questions.
  const isQuizDocumentForToc =
    currentTab?.type === 'quiz' ||
    documentFrontmatter?.kind === 'quiz' ||
    documentFrontmatter?.type === 'quiz';
  const showDocumentToc =
    !isFlowDocumentForToc &&
    !isQuizDocumentForToc &&
    currentTab?.type !== 'integration-basecamp-todo';

  const dispatchBasecampTodoRefresh = () => {
    if (currentTab?.type !== 'integration-basecamp-todo') return;
    window.dispatchEvent(new CustomEvent('basecamp-todo-refresh', {
      detail: {
        todoId: currentTab.integrationData?.todoId || currentTab.id.replace('basecamp-todo-', ''),
        projectId: currentTab.integrationData?.projectId,
      },
    }));
  };

  // --- Sidebar pin helpers (toolbar 3-dots menu) ---
  const _tabType = currentTab?.type as string | undefined
  const isDocumentPinnable = softBookmarkDocId != null
  const isCurrentDocPinned = isDocumentPinnable && sidebarShortcuts.some(s =>
    s.document_id === activeTabId ||
    (currentDocumentPath && s.document_path === currentDocumentPath)
  )
  const handleToolbarPinToggle = async () => {
    try {
      if (isCurrentDocPinned) {
        const shortcut = sidebarShortcuts.find(s =>
          s.document_id === activeTabId ||
          (currentDocumentPath && s.document_path === currentDocumentPath)
        )
        if (shortcut) {
          await deleteSidebarShortcut(shortcut.id)
          toast.success('Unpinned from sidebar')
        }
      } else {
        const idOrPath = currentDocumentPath || activeTabId || ''
        if (!idOrPath) return
        const uuid = await resolveDocumentRowId(idOrPath)
        await createSidebarShortcut({ document_id: uuid })
        toast.success('Pinned to sidebar')
      }
      const next = await getSidebarShortcuts()
      setSidebarShortcuts(next.shortcuts || [])
      window.dispatchEvent(new CustomEvent('sidebar-shortcuts-changed'))
    } catch (e) {
      toast.error(isCurrentDocPinned ? 'Failed to unpin' : 'Failed to pin', {
        description: e instanceof Error ? e.message : 'Unknown error',
      })
    }
  }

  return (
    <InboxThreadChromeProvider>
    <div
      className="h-full flex flex-col overflow-hidden bg-background"
      data-testid={canAcceptSiblingTextImport ? testIds.documentViewer.textImportDropZone : undefined}
      onDragEnter={
        canAcceptSiblingTextImport
          ? (e) => {
              if (!e.dataTransfer.types.includes("Files")) return
              e.preventDefault()
              e.stopPropagation()
              textImportDragDepthRef.current += 1
              setTextImportDropActive(true)
              updateTextImportPlacementFromPointer(e.clientX, e.clientY)
            }
          : undefined
      }
      onDragOver={
        canAcceptSiblingTextImport
          ? (e) => {
              if (!e.dataTransfer.types.includes("Files")) return
              e.preventDefault()
              e.stopPropagation()
              e.dataTransfer.dropEffect = "copy"
              if (!textImportDropActive) setTextImportDropActive(true)
              updateTextImportPlacementFromPointer(e.clientX, e.clientY)
            }
          : undefined
      }
      onDragLeave={
        canAcceptSiblingTextImport
          ? (e) => {
              e.preventDefault()
              e.stopPropagation()
              textImportDragDepthRef.current = Math.max(0, textImportDragDepthRef.current - 1)
              if (textImportDragDepthRef.current === 0) {
                clearTextImportDropState()
              }
            }
          : undefined
      }
      onDrop={
        canAcceptSiblingTextImport
          ? (e) => {
              // Always clear overlay first - empty FileList used to early-return and stick.
              e.preventDefault()
              e.stopPropagation()
              const placement = textImportPlacementRef.current
              const files = Array.from(e.dataTransfer.files ?? [])
              clearTextImportDropState()
              if (files.length === 0 || siblingTextImportFolder == null) return
              window.dispatchEvent(
                new CustomEvent("import-text-drop-at-path", {
                  detail: {
                    folderPath: siblingTextImportFolder,
                    files,
                    tabPlacement: placement,
                    relativeToTabId: activeTabId,
                  },
                }),
              )
            }
          : undefined
      }
    >
      {/* Unified drop chrome: one outline around tab bar + panel (not separate borders) */}
      <div
        className={`flex flex-col flex-1 min-h-0 ${
          textImportDropActive && siblingTextImportFolder != null
            ? "rounded-md outline outline-2 outline-dashed outline-primary outline-offset-[-2px] bg-primary/5"
            : ""
        }`}
      >
      {/* Dedicated Tab Row - Above Document Viewer */}
      <div className="print-hide relative z-20">
        {/* Document Tabs */}
        <DocumentTabs
              openTabs={openTabs}
              activeTabId={tabBarActiveId ?? activeTabId}
              onTabClick={handleTabClick}
              onTabClose={handleTabClose}
              onTabReorder={handleTabReorder}
              onTabDragToChat={handleTabDragToChat}
              onNativeSidebarItemDrop={onNativeSidebarExplorerDropOnTabBar}
              onTabPinToggle={handleTabPinToggle}
              onTabShowFileLocation={onTabShowFileLocation}
              onTabRename={onTabRename}
              onTabMove={onTabMove}
              onTabExportPDF={onTabExportPDF}
              onTabAddToChat={onTabAddToChat}
              onTabCloseOthers={onTabCloseOthers}
              onTabMoveToWindow={onTabMoveToWindow}
              onTabCopyToWindow={onTabCopyToWindow}
              getTabFilePath={getTabFilePath}
              onTabToggleBookmark={onTabToggleBookmark || handleTabToggleBookmark}
              getTabBookmarkStatus={getTabBookmarkStatus || handleGetTabBookmarkStatus}
              onNewTab={handleNewDocument}
              onImportInFolder={handleImportInDefaultFolder}
              onNewFolder={handleNewFolderInDefaultFolder}
              onChooseNewDocumentFolder={handleChooseNewDocumentFolder}
              newDocumentFolderLabel={newDocumentFolderLabel}
              ghostImportTab={
                textImportDropActive && siblingTextImportFolder != null
                  ? {
                      folderPath: siblingTextImportFolder,
                      label: "New import",
                      placement: textImportTabPlacement,
                      relativeToTabId: activeTabId,
                    }
                  : null
              }
            />
      </div>

      {inactiveWorkspaceForActiveDoc ? (
        <div
          className="print-hide flex items-center justify-between gap-3 px-4 py-2 border-b border-amber-500/30 bg-amber-500/10 text-sm"
          data-testid={testIds.documentViewer.inactiveWorkspaceBanner}
          role="status"
        >
          <p className="min-w-0 text-amber-950 dark:text-amber-100">
            <span className="font-medium">{inactiveWorkspaceForActiveDoc.name}</span>
            {' '}
            is hidden in the sidebar. Chat and browse stay scoped to your active workspaces until you enable it.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 border-amber-600/40 bg-background/80"
            data-testid={testIds.documentViewer.inactiveWorkspaceEnableButton}
            onClick={() => toggleWorkspace(inactiveWorkspaceForActiveDoc.id)}
          >
            Enable {inactiveWorkspaceForActiveDoc.name}
          </Button>
        </div>
      ) : null}

      {/* Document Viewer Content */}
      <div
        className="flex-1 min-h-0 flex flex-col relative overflow-auto"
        style={{
          backgroundColor: 'hsl(var(--tab-active-bg))',
        }}
      >
        {textImportDropActive && siblingTextImportFolder != null ? (
          <div
            className="pointer-events-none absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-primary/10 backdrop-blur-[1px]"
            data-testid={testIds.documentViewer.textImportOverlay}
            aria-hidden
          >
            <div className="rounded-md bg-background/95 px-5 py-3 text-center shadow-md max-w-[min(90%,28rem)]">
              <p className="text-sm font-medium text-foreground">
                {textImportDropHeadline}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Import into
              </p>
              {/* NEVER show opaque ws/{hex} storage roots - formatUserFacingStoragePath only */}
              <code className="mt-1.5 block break-all text-xs text-primary">
                {siblingTextImportFolderLabel}
              </code>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Hover the tab bar: left / replace / right
              </p>
            </div>
          </div>
        ) : null}
        {/* Document List Toggle Button - Only when list is hidden */}
        {!showDocumentList && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDocumentListToggle}
            className="absolute left-4 top-4 h-8 px-3 bg-background/90 backdrop-blur-sm border border-border/50 hover:bg-accent z-10 shadow-sm"
            title="Show Document List"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            <span className="text-sm">Document List</span>
          </Button>
        )}

        {/* Toolbar - Hide for graph view, home tab, full inbox tab, search (inbox-thread has its own) */}
        {(() => {
          const currentTab = openTabs.find(tab => tab.id === activeTabId);
          const isGraphView = activeTabId === 'graph' || currentTab?.type === 'graph-view';
          const isHomeTab = currentTab?.type === 'home';
          const isFlowIndexTab = currentTab?.type === 'flow-index';
          const isInboxTab = currentTab?.type === 'inbox';
          const isInboxThreadTab = currentTab?.type === 'inbox-thread';
          const isChatConversationTab = currentTab?.type === 'chat-conversation';
          const isSearchView = currentTab?.type === 'search-view';
          const isPersonView = currentTab?.type === 'person-view';
          const isEditHistory =
            currentTab?.type === 'edit-history' ||
            activeTabId === 'edit-history' ||
            activeTabId === 'view:edit-history';
          const isProductBoardTab = currentTab?.type === 'integration-product-board';
          const isDriveFolderTab = currentTab?.type === 'integration-google-drive';
          const isNextcloudDocument = isNextcloudDocumentTab(currentTab);
          const isNextcloudFolderTab = currentTab?.type === 'integration-nextcloud' && !isNextcloudDocument;
          const isDriveFilePreview = currentTab?.type === 'integration-google-drive-preview';
          const isPlatformSkillTab = currentTab?.type === 'platform-skill';
          const hideDocumentFileActions = shouldHideDocumentFileActions(currentTab)
          const showToolbarSave =
            activeRendererCapabilities.saveable &&
            currentTab?.type !== 'integration-basecamp-todo' &&
            currentTab?.type !== 'integration-basecamp-triage' &&
            !hideDocumentFileActions
          const showToolbarFlow =
            (currentTab?.type === 'flow' ||
              documentFrontmatter?.kind === 'flow' ||
              documentFrontmatter?.type === 'flow') &&
            !!activeTabId
          const showToolbarInfo =
            isNextcloudDocument ||
            activeTab?.type === 'integration-basecamp-todo' ||
            (!!activeTabId &&
              !isPlatformSkillTab &&
              !activeTabId.startsWith('integration-') &&
              !activeTabId.startsWith('basecamp-'))
          const showToolbarCursor =
            activeRendererCapabilities.openInCursor &&
            !!basecampTodoId &&
            currentTab?.type !== 'integration-basecamp-todo' &&
            !!(projectName || basecampProjectId)
          const refreshAction = resolveViewerRefreshAction(currentTab)
          const DriveOpenIcon = resolveOpenExternalIcon(GoogleDriveIcon)

          if (
            isGraphView ||
            isHomeTab ||
            isFlowIndexTab ||
            isInboxTab ||
            isSearchView ||
            isPersonView ||
            isEditHistory ||
            isDriveFolderTab ||
            isNextcloudFolderTab
          ) {
            return null
          }

          if (isInboxThreadTab) {
            return (
              <InboxThreadToolbar
                toolbarRef={toolbarRef}
                title={currentTab?.title ?? 'Inbox thread'}
                containerWidth={containerWidth}
                canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
                canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
                onGoBack={parentGoBack || handleGoBack}
                onGoForward={parentGoForward || handleGoForward}
              />
            )
          }

          if (isChatConversationTab && currentTab?.chatThreadId) {
            const chatThreadId = currentTab.chatThreadId
            return (
              <ChatConversationToolbar
                toolbarRef={toolbarRef}
                threadId={chatThreadId}
                title={currentTab.title ?? 'New chat'}
                containerWidth={containerWidth}
                canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
                canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
                onGoBack={parentGoBack || handleGoBack}
                onGoForward={parentGoForward || handleGoForward}
                hasActiveConversation={!chatThreadId.startsWith('new-')}
                hasChatMessages={!chatThreadId.startsWith('new-')}
              />
            )
          }

          return (
          <>
          <nav
            ref={toolbarRef}
            className="flex items-center justify-between px-4 py-2 border-b border-border bg-background/50 print-hide document-viewer-toolbar"
            aria-label="Document actions"
            data-testid={testIds.documentViewer.toolbar}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Navigation buttons */}
              <NavigationButtons
                onGoBack={parentGoBack || handleGoBack}
                onGoForward={parentGoForward || handleGoForward}
                canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
                canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
                backTestId={testIds.documentViewer.backButton}
                forwardTestId={testIds.documentViewer.forwardButton}
              />

              {/* Breadcrumbs and Title Container - matches folder view layout */}
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                {isNextcloudDocument && currentTab?.integrationData?.nextcloudPath ? (
                  <NextcloudDocumentCrumbs
                    path={currentTab.integrationData.nextcloudPath}
                    organizationId={currentTab.integrationData.organizationId ?? currentTab.workspaceId ?? undefined}
                    containerWidth={containerWidth}
                  />
                ) : (
                <DocumentToolbarBreadcrumbs
                  currentDocumentPath={currentDocumentPath}
                  currentTab={currentTab}
                  activeTabId={activeTabId}
                  containerWidth={containerWidth}
                  basecampTodoId={basecampTodoId}
                  workspaces={workspacesRoute}
                  openTabs={openTabs}
                  setOpenTabs={setOpenTabs}
                  setActiveTabId={setActiveTabId}
                  navigation={navigation}
                  navigate={navigate}
                  onFolderClick={onFolderClick}
                />
                )}

                {/* Path crumbs. A todo or todo-list title is the heading below, same as a document. */}
              {shouldShowBasecampChromeBreadcrumbs(currentTab) && currentTab ? (
                <BasecampIntegrationBreadcrumbs
                  currentTab={currentTab}
                  containerWidth={containerWidth}
                />
              ) : null}
              {shouldShowBasecampChromeBreadcrumbs(currentTab) &&
              _tabType !== "integration-basecamp-todo" &&
              _tabType !== "integration-basecamp-todo-list" ? null : editingTitle && _tabType !== "explorer" && _tabType !== "integration-basecamp" && _tabType !== "integration-basecamp-todo" && _tabType !== "integration-basecamp-todo-list" && _tabType !== "integration-basecamp-triage" ? (
                <Input
                  ref={titleInputRef}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={handleTitleKeyDown}
                  className="text-lg font-semibold h-9 px-2 max-w-md"
                  aria-label="Document title"
                  data-testid={testIds.documentViewer.titleInput}
                />
              ) : (() => {
                const currentTitle = currentTab?.type === "explorer"
                  ? "Explorer"
                  : openTabs.find(tab => tab.id === activeTabId)?.title || "Untitled"

                // Determine max width based on container width
                const getMaxWidth = () => {
                  if (containerWidth === null) return "max-w-md"
                  if (containerWidth > 800) return "max-w-[600px]"
                  if (containerWidth > 600) return "max-w-[400px]"
                  return "max-w-[300px]"
                }

                // Don't allow editing for Basecamp integration tabs
                const isBasecampIntegration = _tabType === 'integration-basecamp' ||
                                              _tabType === 'integration-basecamp-todo' ||
                                              _tabType === 'integration-basecamp-todo-list' ||
                                              _tabType === 'integration-basecamp-triage'
                const isEditable = currentTab?.type !== "explorer" && !isBasecampIntegration && currentTab?.type !== 'integration-google-drive-preview' && currentTab?.type !== 'platform-skill' && !isNextcloudDocument

                const todoCompleted =
                  _tabType === 'integration-basecamp-todo' &&
                  Boolean(currentTab?.integrationData?.todoData?.completed)

                return (
                  <div className="flex min-w-0 items-center gap-2">
                    {todoCompleted ? (
                      <CheckCircle2
                        className="h-5 w-5 shrink-0 text-green-500"
                        aria-label="Completed"
                        data-testid={testIds.documentViewer.titleCompleted}
                      />
                    ) : null}
                    <h1
                      className={`text-lg font-semibold text-foreground ${isEditable ? 'cursor-pointer hover:text-primary' : ''} transition-colors truncate min-w-0`}
                      onClick={isEditable ? handleTitleEdit : undefined}
                      title={isEditable ? `Click to edit title: ${currentTitle}` : ""}
                      aria-label={isEditable ? `Document title: ${currentTitle}, click to edit` : `Document title: ${currentTitle}`}
                      data-testid={testIds.documentViewer.title}
                    >
                      {currentTitle}
                    </h1>
                  </div>
                )
              })()}
              </div>
            </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* View mode buttons - COMMENTED OUT FOR NOW */}
            {/*
            <Button
              variant={viewMode === "single" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("single")}
              title="Single view"
            >
              <FileText className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "double" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("double")}
              title="Split view"
            >
              <Columns2 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "diff" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("diff")}
              title="Diff view"
            >
              <GitCompare className="h-4 w-4" />
            </Button>

            <div className="w-px h-6 bg-border mx-2" />
            */}

            {/* Persistent row: Save + view-state toggles only. Refresh, Basecamp, Copy-path,
                and Google Drive push/pull are document actions and live in the overflow (⋯)
                menu - see docs/product/bets/2026-07-04-entity-menu-registry-hardening.md (Phase 2). */}
                  {showToolbarSave && (
                    <IconButton
                      icon={<Save className="h-4 w-4" />}
                      label="Save document"
                      shortcut="⌘S"
                      variant="ghost"
                      size="sm"
                      onClick={handleManualSave}
                      disabled={documentSaveState.saving || documentSaveState.isDeleted}
                      loading={documentSaveState.saving}
                      data-testid={testIds.documentViewer.saveButton}
                    />
                  )}

                  {isDriveFilePreview ? (
                    <>
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent(DRIVE_PREVIEW_OPEN_IMPORT_EVENT))
                        }}
                        data-testid={testIds.driveSidebar.drivePreviewImportButton}
                      >
                        {currentTab?.integrationData?.importedDocumentId
                          ? 'Open in Maater'
                          : 'Import to Maater'}
                      </Button>
                      <IconButton
                        icon={<MessageSquare className="h-4 w-4" />}
                        label="Comments"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (showInfoPane && infoDockMode === 'comments') {
                            setShowInfoPane(false)
                            setInfoDockMode('details')
                          } else {
                            setInfoDockMode('comments')
                            setShowInfoPane(true)
                          }
                        }}
                        data-testid={testIds.driveSidebar.drivePreviewCommentsToggle}
                        className={
                          showInfoPane && infoDockMode === 'comments'
                            ? 'bg-primary/10 hover:bg-primary/15 text-primary'
                            : ''
                        }
                      />
                    </>
                  ) : null}

                  {showToolbarFlow && activeTabId ? (
                    <FlowRuntimeControl flowId={activeTabId} />
                  ) : null}

                  {showToolbarSave || isDriveFilePreview || showToolbarFlow ? (
                    <div className="w-px h-4 bg-border mx-1" />
                  ) : null}

                  {/* Info/edit button: owns the right dock (Details | History). Outline lives in ⋮. */}
                  {showToolbarInfo && (
                    activeTab?.type === 'integration-basecamp-todo' ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Edit todo"
                        aria-pressed={showInfoPane}
                        onClick={() => {
                          if (showInfoPane) {
                            setShowInfoPane(false)
                            setInfoDockMode('details')
                          } else {
                            setInfoDockMode('details')
                            setShowInfoPane(true)
                          }
                        }}
                        data-testid={testIds.documentViewer.infoButton}
                        className={`h-7 px-2 text-xs font-medium ${
                          showInfoPane
                            ? 'bg-primary/10 hover:bg-primary/15 text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Edit
                      </Button>
                    ) : (
                      <IconButton
                        icon={<Info className="h-4 w-4" />}
                        label="Toggle info pane"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (showInfoPane) {
                            setShowInfoPane(false)
                            setInfoDockMode('details')
                          } else {
                            setInfoDockMode('details')
                            setShowInfoPane(true)
                          }
                        }}
                        data-testid={testIds.documentViewer.infoButton}
                        className={showInfoPane ? "bg-primary/10 hover:bg-primary/15 text-primary" : ""}
                      />
                    )
                  )}

                  {showToolbarCursor && basecampTodoId ? (
                      <CursorWorkspaceMenu
                        todoId={basecampTodoId}
                        todoTitle={
                          basecampTodoTitle ||
                          openTabs.find((t) => t.id === activeTabId)?.title ||
                          null
                        }
                        projectName={projectName}
                        basecampProjectId={basecampProjectId}
                        documentId={documentDbId || activeTabId}
                        folderPath={
                          parentFolderFromDocumentPath(currentDocumentPath) ||
                          (currentDocumentPath
                            ? currentDocumentPath.replace(/\/[^/]+$/, '') || null
                            : null)
                        }
                        compact={(containerWidth ?? 999) < 700}
                        data-testid={testIds.documentViewer.cursorWorkspaceMenu}
                      />
                    ) : null}

                  {showToolbarInfo || showToolbarCursor ? (
                    <div className="w-px h-4 bg-border mx-1" />
                  ) : null}

                  <span className="relative inline-flex">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton
                          icon={<MoreVertical className="h-4 w-4" />}
                          label={
                            !isProductBoardTab && externalChangeDetection.hasExternalChanges
                              ? "More options - document changed externally"
                              : "More options"
                          }
                          variant="ghost"
                          size="sm"
                          data-testid={testIds.documentViewer.menuButton}
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                      >
                {isDriveFilePreview && currentTab?.integrationData?.driveWebViewLink ? (
                  <DropdownMenuItem asChild data-testid={testIds.driveSidebar.drivePreviewOpenInDriveMenuItem}>
                    <a
                      href={currentTab.integrationData.driveWebViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DriveOpenIcon className="mr-2 h-4 w-4" />
                      Open in Drive
                    </a>
                  </DropdownMenuItem>
                ) : null}
                {isDriveFilePreview && currentTab?.integrationData?.driveWebViewLink ? (
                  <DropdownMenuSeparator />
                ) : null}
                {showDocumentToc && !hideDocumentFileActions ? (
                  <DropdownMenuItem
                    onClick={() => setTocOpen((v) => !v)}
                    data-testid={testIds.documentViewer.outlineMenuItem}
                  >
                    <List className="mr-2 h-4 w-4" />
                    {tocOpen ? 'Hide outline' : 'Show outline'}
                  </DropdownMenuItem>
                ) : null}
                {showDocumentToc && !hideDocumentFileActions ? <DropdownMenuSeparator /> : null}
                {refreshAction ? (
                  <DropdownMenuItem
                    onClick={() => void handleExternalRefresh()}
                    disabled={
                      isProductBoardTab
                        ? documentSaveState.isDeleted
                        : refreshAction.label === 'Refresh document'
                          ? externalChangeDetection.isRefreshing ||
                            documentSaveState.isDeleted ||
                            (Boolean(gdocLinkedId) && gdocSync.activity === 'working')
                          : false
                    }
                    data-testid={testIds.documentViewer.refreshMenuItem}
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${
                        isProductBoardTab
                          ? ''
                          : refreshAction.label === 'Refresh document' &&
                              externalChangeDetection.hasExternalChanges
                          ? 'text-blue-500 animate-pulse'
                          : refreshAction.label === 'Refresh document' &&
                              (externalChangeDetection.isRefreshing ||
                                (Boolean(gdocLinkedId) && gdocSync.activity === 'working'))
                          ? 'animate-spin'
                          : ''
                      }`}
                    />
                    {refreshAction.label === 'Refresh document'
                      ? externalChangeDetection.hasExternalChanges
                        ? 'Document changed externally - click to refresh'
                        : externalChangeDetection.isRefreshing ||
                            (Boolean(gdocLinkedId) && gdocSync.activity === 'working')
                          ? gdocLinkedId
                            ? 'Refreshing & syncing…'
                            : 'Refreshing...'
                          : gdocLinkedId
                            ? 'Refresh & sync'
                            : refreshAction.label
                      : refreshAction.label}
                  </DropdownMenuItem>
                ) : null}
                {currentTab?.type === 'integration-basecamp' &&
                !currentTab.integrationData?.projectId ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => dispatchIndexPanelSearch()}
                      data-testid={testIds.documentViewer.indexSearchMenuItem}
                    >
                      <Search className="mr-2 h-4 w-4" />
                      Search
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {panelSearchShortcutHint()}
                      </span>
                    </DropdownMenuItem>
                    {resolveBasecampIndexSection(currentTab) === 'activity' ? (
                      <>
                        <DropdownMenuItem
                          onClick={() => dispatchIndexViewAction('listening')}
                          data-testid={testIds.documentViewer.activityListeningMenuItem}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Listening settings
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => dispatchIndexViewAction('open-external')}
                          data-testid={testIds.documentViewer.activityOpenBasecampMenuItem}
                        >
                          <OpenInBasecampIcon className="mr-2 h-4 w-4" />
                          Open project in Basecamp
                        </DropdownMenuItem>
                      </>
                    ) : null}
                    {resolveBasecampIndexSection(currentTab) === 'projects' ? (
                      <>
                        <DropdownMenuItem
                          onClick={() => dispatchIndexViewAction('reload-list')}
                          data-testid={testIds.basecamp.projectsReloadList}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Reload list
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => dispatchIndexViewAction('refresh-membership')}
                          data-testid={testIds.basecamp.projectsRefreshMembership}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Refresh membership
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => dispatchIndexViewAction('refresh-visible')}
                          data-testid={testIds.basecamp.projectsRefreshVisible}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Refresh visible
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </>
                ) : null}
                {!isArchivedDoc && showGoogleDriveMenu && !hideDocumentFileActions && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid={testIds.documentViewer.gdocMenuSubTrigger}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Google Drive
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                      className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                    >
                      {!gdocLinkedId ? (
                        <DropdownMenuItem
                          onClick={() => void openPublishPicker()}
                          disabled={isPublishingToDrive}
                          data-testid={testIds.documentViewer.gdocPublishMenuItem}
                        >
                          {isPublishingToDrive ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="mr-2 h-4 w-4" />
                          )}
                          Publish to Drive...
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              window.open(
                                `https://docs.google.com/document/d/${encodeURIComponent(gdocLinkedId)}/edit`,
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }}
                            data-testid={testIds.documentViewer.gdocOpenInDriveMenuItem}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open in Google Drive
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void gdocSync.runSync({ interactive: true })}
                            disabled={gdocSync.activity === 'working' || isPullingFromDrive || isPushingToDrive}
                            data-testid={testIds.documentViewer.gdocSyncMenuItem}
                          >
                            {gdocSync.activity === 'working' ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="mr-2 h-4 w-4" />
                            )}
                            {gdocSync.keepSynced ? 'Sync now' : 'Sync with Drive'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void gdocSync.toggleKeepSynced()}
                            disabled={gdocSync.activity === 'working'}
                            data-testid={testIds.documentViewer.gdocKeepSyncedMenuItem}
                          >
                            {gdocSync.keepSynced ? 'Turn off Keep synced' : 'Keep synced'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => void handlePullFromDrive()}
                            disabled={isPullingFromDrive || isPushingToDrive || gdocSync.activity === 'working'}
                            data-testid={testIds.documentViewer.pullFromDriveMenuItem}
                          >
                            {isPullingFromDrive ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : pullFromDriveSuccess ? (
                              <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                            ) : (
                              <CloudDownload className="mr-2 h-4 w-4" />
                            )}
                            {pullFromDriveSuccess ? 'Pulled!' : 'Force pull from Drive'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={handlePushToDrive}
                            disabled={isPushingToDrive || isPullingFromDrive || gdocSync.activity === 'working'}
                            data-testid={testIds.documentViewer.pushToDriveMenuItem}
                          >
                            {isPushingToDrive ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : pushToDriveSuccess ? (
                              <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                            ) : (
                              <CloudUpload className="mr-2 h-4 w-4" />
                            )}
                            {pushToDriveSuccess ? 'Pushed!' : 'Force push to Drive'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={handleDisconnectFromDrive}
                            disabled={isDisconnectingFromDrive || gdocSync.activity === 'working'}
                            className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
                            data-testid={testIds.documentViewer.gdocDisconnectMenuItem}
                          >
                            {isDisconnectingFromDrive ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Unlink className="mr-2 h-4 w-4" />
                            )}
                            Disconnect from Drive
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                {!isArchivedDoc && !hideDocumentFileActions &&
                  ttsConfigured &&
                  !isGeneratingAudio &&
                  generatedAudioDocId !== (documentDbId || activeTabId) &&
                  !/```(?:yaml\s+audio|audio)\b/.test(documentBodyForChangelog || '') &&
                  String(documentFrontmatter?.type || '') !== 'audio' &&
                  !/\.mp3$/i.test(currentDocumentPath || '') && (
                  <DropdownMenuItem
                    onClick={() => void handleGenerateAudio()}
                    disabled={isGeneratingAudio}
                    data-testid={testIds.documentViewer.generateAudio}
                  >
                    {isGeneratingAudio ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Volume2 className="mr-2 h-4 w-4" />
                    )}
                    {isGeneratingAudio ? 'Generating audio...' : 'Generate audio'}
                  </DropdownMenuItem>
                )}
                {!isArchivedDoc && !hideDocumentFileActions && (
                  <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleUndo}
                  disabled={undoState.undoing || !undoState.hasHistory || documentSaveState.isDeleted}
                  data-testid={testIds.documentViewer.undoButton}
                >
                  <Undo className="mr-2 h-4 w-4" />
                  Undo last edit
                  <span className="ml-auto text-xs text-muted-foreground">⌘Z</span>
                </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={onFullscreenToggle}>
                  {isFullscreen ? (
                    <Minimize2 className="mr-2 h-4 w-4" />
                  ) : (
                    <Maximize2 className="mr-2 h-4 w-4" />
                  )}
                  {isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                </DropdownMenuItem>
                {!hideDocumentFileActions ? (
                <>
                <DropdownMenuSeparator />

                <DropdownMenuLabel className={QUIET_OVERFLOW_MENU_LABEL_CLASS}>
                  Location
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={handleShowFileLocation}
                  disabled={!currentDocumentPath}
                  data-testid={testIds.documentViewer.showFileLocationMenuItem}
                >
                  <Folder className="mr-2 h-4 w-4" />
                  Show file location
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (currentDocumentPath) {
                      window.dispatchEvent(new CustomEvent('reveal-in-sidebar', { detail: { filePath: currentDocumentPath } }))
                    }
                  }}
                  disabled={!currentDocumentPath}
                  data-testid={testIds.documentViewer.revealInSidebarMenuItem}
                >
                  <PanelLeft className="mr-2 h-4 w-4" />
                  Reveal in sidebar
                </DropdownMenuItem>
                {!isArchivedDoc && isDocumentPinnable && (
                  <DropdownMenuItem onClick={handleToolbarPinToggle}>
                    {isCurrentDocPinned
                      ? <PinOff className="mr-2 h-4 w-4" />
                      : <Pin className="mr-2 h-4 w-4" />}
                    {isCurrentDocPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
                  </DropdownMenuItem>
                )}
                {!isArchivedDoc && softBookmarkDocId && (
                  <DocumentSoftBookmarkMenuSection documentId={softBookmarkDocId} />
                )}
                {!isArchivedDoc && (
                  <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className={QUIET_OVERFLOW_MENU_LABEL_CLASS}>
                  Document
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={handleRenameClick}
                  disabled={!currentDocumentPath || currentTab?.type === 'explorer'}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (currentDocumentPath) {
                      setMoveDialogOpen(true)
                    }
                  }}
                  disabled={!currentDocumentPath || currentTab?.type === 'explorer'}
                >
                  <FolderOpen className="mr-2 h-4 w-4" />
                  Move document
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    if (!currentTab) return
                    try {
                      const url = absoluteAppUrlForTab(currentTab, workspacesRoute)
                      await navigator.clipboard.writeText(url)
                      toast.success('URL copied!', { description: url, duration: 2000 })
                    } catch {
                      toast.error('Failed to copy URL')
                    }
                  }}
                  disabled={!currentTab}
                  data-testid={
                    activeRendererKind === 'image'
                      ? testIds.imageDocument.copyUrl
                      : testIds.documentViewer.copyUrlMenuItem
                  }
                >
                  <Link className="mr-2 h-4 w-4" />
                  Copy URL
                </DropdownMenuItem>
                {cursorExtensionEnabled && currentTab?.filePath && (
                  <DropdownMenuItem
                    onClick={() => {
                      const uri = `flownotes:${currentTab.filePath}`
                      navigator.clipboard.writeText(uri)
                      toast.success('Path copied', { description: uri })
                    }}
                    data-testid={testIds.documentViewer.copyPathMenuItem}
                  >
                    <Link className="mr-2 h-4 w-4" />
                    Copy document path
                  </DropdownMenuItem>
                )}
                {activeRendererCapabilities.download === 'original' ? (
                  <DropdownMenuItem
                    onClick={() => void handleOriginalDocumentDownload()}
                    disabled={!activeTabId || activeTabId.startsWith('new-')}
                    data-testid={testIds.imageDocument.download}
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    Download image
                  </DropdownMenuItem>
                ) : activeRendererCapabilities.download === 'pdf' ? (
                  <DropdownMenuItem
                    onClick={handleExportPDF}
                    disabled={currentTab?.type === 'explorer' || currentTab?.type === 'home' || !activeTabId || activeTabId.startsWith('new-')}
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </DropdownMenuItem>
                ) : null}
                  </>
                )}
                </>
                ) : null}
                {/* Basecamp options - Show when todo_id exists (frontmatter or path-derived) */}
                {!isArchivedDoc && (basecampTodoId && currentTab?.type !== 'integration-basecamp-todo') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={async () => {
                        const detectRecordType = (): 'todo' | 'todolist' => {
                          const fm = documentFrontmatter
                          if (fm?.type === 'todolist' || fm?.original_type === 'todolist') return 'todolist'
                          if (fm?.todolist_id && String(fm.todolist_id) === basecampTodoId) return 'todolist'
                          return 'todo'
                        }
                        if (basecampProjectId) {
                          openBasecampTodoInFlowNotes(basecampTodoId, basecampProjectId, detectRecordType())
                        } else {
                          try {
                            const res = await fetchWithAuth(`/api/basecamp/search-todo/${basecampTodoId}`)
                            const data = res.ok ? await res.json() : null
                            if (data?.found && data?.todo?.projectId) {
                              const rt = (data.todo.recordType as 'todo' | 'todolist') || detectRecordType()
                              openBasecampTodoInFlowNotes(basecampTodoId, String(data.todo.projectId), rt)
                            } else {
                              toast.error("Could not open todo", { description: "Basecamp project ID could not be resolved." })
                            }
                          } catch {
                            toast.error("Could not open todo", { description: "Failed to resolve Basecamp project." })
                          }
                        }
                      }}
                    >
                      <OpenInMaaterIcon className="mr-2 h-4 w-4" />
                      {basecampViewTodoLabel()}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const url = resolveExternalBasecampUrlForDocument({
                          basecampId: basecampTodoId,
                          projectId: basecampProjectId,
                          frontmatter: documentFrontmatter,
                          filePath: currentDocumentPath,
                          sourceUrl: documentFrontmatter?.source_url || documentSourceUrl,
                        })
                        if (url) {
                          window.open(url, '_blank');
                        } else {
                          toast.error("No Basecamp URL available", { description: "Project ID is not resolved yet." })
                        }
                      }}
                    >
                      <OpenInBasecampIcon className="mr-2 h-4 w-4" />
                      {BASECAMP_OPEN_IN_BASECAMP_LABEL}
                    </DropdownMenuItem>
                  </>
                )}
                {/* Basecamp Project Viewer options - Show when viewing Basecamp project tab */}
                {currentTab?.type === 'integration-basecamp' && currentTab.integrationData?.projectId && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        // Open git config dialog - we'll use a custom event
                        window.dispatchEvent(new CustomEvent('basecamp-project-configure-git', {
                          detail: {
                            projectId: currentTab.integrationData?.projectId,
                            projectName: currentTab.title
                          }
                        }));
                      }}
                    >
                      <Code2 className="mr-2 h-4 w-4" />
                      Configure Git Path
                    </DropdownMenuItem>
                  </>
                )}
                {/* Basecamp Todo Viewer options - Show when viewing Basecamp todo tab */}
                {currentTab?.type === 'integration-basecamp-todo' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={dispatchBasecampTodoRefresh}
                      data-testid={testIds.basecamp.todoToolbarRefresh}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </DropdownMenuItem>
                    {currentTab.integrationData?.projectId && currentTab.integrationData?.todoId && (
                      <DropdownMenuItem
                        onClick={() => {
                          const projectId = currentTab.integrationData?.projectId;
                          const todoId = currentTab.integrationData?.todoId;
                          const url = buildBasecampWebUrl(todoId!, projectId!);
                          window.open(url, '_blank');
                        }}
                      >
                        <OpenInBasecampIcon className="mr-2 h-4 w-4" />
                        {BASECAMP_OPEN_IN_BASECAMP_LABEL}
                      </DropdownMenuItem>
                    )}
                    {currentTab.integrationData?.todoId && activeTabId ? (
                      <DropdownMenuItem
                        onClick={() => onTabAddToChat?.(activeTabId)}
                        data-testid={testIds.basecamp.todoAddToCurrentChat}
                      >
                        <AddToCurrentChatIcon className="mr-2 h-4 w-4" />
                        {ADD_TO_CURRENT_CHAT_LABEL}
                      </DropdownMenuItem>
                    ) : null}
                    {(currentTab.integrationData?.projectName || currentTab.integrationData?.projectId) && (
                      <CursorWorkspaceDropdown
                        asSubmenu
                        todoId={currentTab.integrationData?.todoId}
                        todoTitle={currentTab.title}
                        projectName={currentTab.integrationData?.projectName || currentTab.title}
                        projectId={currentTab.integrationData?.projectId}
                      />
                    )}
                    {currentTab.integrationData?.projectId && (
                      <DropdownMenuItem
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('basecamp-project-configure-git', {
                            detail: {
                              projectId: currentTab.integrationData?.projectId,
                              projectName: currentTab.integrationData?.projectName || currentTab.title,
                            },
                          }));
                        }}
                      >
                        <Code2 className="mr-2 h-4 w-4" />
                        Configure Git Path
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {!isArchivedDoc && !hideDocumentFileActions && (
                  <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleAddToChatClick}
                  disabled={!currentDocumentPath || currentTab?.type === 'explorer'}
                >
                  <AddToCurrentChatIcon className="mr-2 h-4 w-4" />
                  {ADD_TO_CURRENT_CHAT_LABEL}
                </DropdownMenuItem>
                {isBetDispatchReady && !isCloudDispatchRunning && (
                  <DropdownMenuItem
                    onClick={handleDispatchToCloudFromViewer}
                    disabled={isDispatchingToCloud}
                    className="gap-2"
                    data-testid={testIds.cloudDispatch.dispatchButton}
                  >
                    {isDispatchingToCloud
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <span className="mr-2 text-sm leading-none">⚡</span>
                    }
                    Dispatch to Cloud Agent
                  </DropdownMenuItem>
                )}
                {/* Bookmark toggle - show if document has Basecamp todo OR if viewing Basecamp todo tab */}
                {((basecampTodoId && basecampProjectId) || currentTab?.type === 'integration-basecamp-todo') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleToggleBookmark}
                      disabled={isTogglingBookmark || isBookmarked === null}
                    >
                      {isTogglingBookmark ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {isBookmarked ? 'Removing...' : 'Adding...'}
                        </>
                      ) : isBookmarked ? (
                        <>
                          <BookmarkX className="mr-2 h-4 w-4" />
                          Remove Bookmark
                        </>
                      ) : (
                        <>
                          <Bookmark className="mr-2 h-4 w-4" />
                          Add Bookmark
                        </>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
                  </>
                )}
                {!hideDocumentFileActions ? (
                  <>
                <DropdownMenuSeparator />

                {/* Document Lifecycle Actions - quiet Ink labels; Delete accents only on hover/focus */}
                {isArchivedDoc ? (
                  <DropdownMenuItem
                    onClick={() => void handleUnarchiveDocument()}
                    disabled={
                      isArchivingOrRestoring ||
                      currentTab?.type === 'explorer' ||
                      currentTab?.type === 'home' ||
                      !activeTabId ||
                      activeTabId.startsWith('new-')
                    }
                    data-testid={testIds.documentViewer.unarchiveMenuItem}
                  >
                    {isArchivingOrRestoring ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArchiveRestore className="mr-2 h-4 w-4" />
                    )}
                    Unarchive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => void handleArchiveDocument()}
                    disabled={
                      isArchivingOrRestoring ||
                      currentTab?.type === 'explorer' ||
                      currentTab?.type === 'home' ||
                      !activeTabId ||
                      activeTabId.startsWith('new-')
                    }
                    data-testid={testIds.documentViewer.archiveMenuItem}
                  >
                    {isArchivingOrRestoring ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    Archive document
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={handleDeleteClick}
                  disabled={currentTab?.type === 'explorer' || currentTab?.type === 'home' || !activeTabId || activeTabId.startsWith('new-')}
                  className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete document
                </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
                    </DropdownMenu>
                    {!isProductBoardTab && externalChangeDetection.hasExternalChanges && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full border-2 border-background pointer-events-none" aria-hidden />
                    )}
                  </span>
          </div>
        </nav>
          {isArchivedDoc &&
            currentTab?.type !== 'explorer' &&
            currentTab?.type !== 'home' && (
            <div
              className="print-hide flex flex-wrap items-center justify-between gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-950 dark:text-amber-100"
              data-testid={testIds.documentViewer.archivedBanner}
              role="status"
            >
              <p className="min-w-0">
                <span className="font-medium">Archived</span>
                {' - '}
                this document is in the archive. Unarchive to restore it to its previous folder.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 border-amber-600/40 bg-background/80"
                data-testid={testIds.documentViewer.archivedBannerUnarchiveButton}
                disabled={isArchivingOrRestoring}
                onClick={() => void handleUnarchiveDocument()}
              >
                {isArchivingOrRestoring ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArchiveRestore className="mr-2 h-4 w-4" />
                )}
                Unarchive
              </Button>
            </div>
          )}
          </>
          );
        })()}

        {/* Content Area - Render all tabs but hide inactive ones */}
        <DocumentViewerMainContent
          openTabs={openTabs}
          activeTabId={activeTabId}
          activeTab={activeTab}
          viewMode={viewMode}
          workspaces={workspaces}
          basecampProjects={basecampProjects}
          activeWorkspaceIds={activeWorkspaceIds}
          configuredIntegrations={configuredIntegrations}
          showInfoPane={showInfoPane}
          infoDockMode={infoDockMode}
          setInfoDockMode={setInfoDockMode}
          infoPanelRef={infoPanelRef}
          documentPanelSizes={documentPanelSizes}
          setDocumentPanelSizes={setDocumentPanelSizes}
          documentFrontmatter={documentFrontmatter}
          setDocumentFrontmatter={setDocumentFrontmatter}
          documentBodyForChangelog={documentBodyForChangelog}
          setDocumentBodyForChangelog={setDocumentBodyForChangelog}
          showDocumentToc={showDocumentToc}
          tocOpen={tocOpen}
          setTocOpen={setTocOpen}
          tocHeadings={tocHeadings}
          tocContentRef={tocContentRef}
          scrollElRef={scrollElRef}
          markdownBodyViewMode={markdownBodyViewMode}
          setMarkdownBodyViewMode={setMarkdownBodyViewMode}
          documentSaveState={documentSaveState}
          cloudDispatch={cloudDispatch}
          isDispatchingToCloud={isDispatchingToCloud}
          setWatchLiveAgentId={setWatchLiveAgentId}
          searchBoostOpenTabIds={searchBoostOpenTabIds}
          searchBoostRecentlyViewedIds={searchBoostRecentlyViewedIds}
          handleDocumentClickWithEvent={handleDocumentClickWithEvent}
          handleDocumentClick={handleDocumentClick}
          handleNewDocument={handleNewDocument}
          onFolderClick={onFolderClick}
          parentDocumentClick={parentDocumentClick}
          parentGoBack={parentGoBack}
          parentGoForward={parentGoForward}
          handleGoBack={handleGoBack}
          handleGoForward={handleGoForward}
          navigation={navigation}
          onUrlChange={onUrlChange}
          markProgrammaticNavigation={markProgrammaticNavigation}
          setOpenTabs={setOpenTabs}
          setActiveTabId={setActiveTabId}
          handleAIDocumentSave={handleAIDocumentSave}
          handleAIDocumentCancel={handleAIDocumentCancel}
          handleSendToChat={handleSendToChat}
          handleInternalLinkClick={handleInternalLinkClick}
          handleManualSave={handleManualSave}
          completeManualSave={completeManualSave}
          handleFrontmatterChange={handleFrontmatterChange}
          onDocumentMetaSync={handleDocumentMetaSync}
          handleDeleteClick={handleDeleteClick}
          handleCloseInfoPane={handleCloseInfoDock}
          setBasecampTodoTitle={setBasecampTodoTitle}
          setBasecampTodoTitleVisible={setBasecampTodoTitleVisible}
          driveSyncActivity={gdocLinkedId ? gdocSync.activity : undefined}
          driveSyncLabel={gdocLinkedId ? gdocSync.activityLabel : undefined}
          driveGdocId={gdocLinkedId}
          driveOrganizationId={publishOrgId}
          driveLastSynced={
            typeof documentFrontmatter?.gdoc_last_synced === 'string'
              ? documentFrontmatter.gdoc_last_synced
              : null
          }
          driveLastPushed={
            typeof documentFrontmatter?.gdoc_last_pushed === 'string'
              ? documentFrontmatter.gdoc_last_pushed
              : null
          }
          driveLastPulled={
            typeof documentFrontmatter?.gdoc_last_pulled === 'string'
              ? documentFrontmatter.gdoc_last_pulled
              : null
          }
          driveKeepSynced={gdocLinkedId ? gdocSync.keepSynced : undefined}
        />

        {/* Move Dialog */}
        <FolderPickerDialog
          open={moveDialogOpen}
          onOpenChange={setMoveDialogOpen}
          currentPath={currentDocumentPath || ''}
          onSelect={handleMove}
          workspaces={moveDialogWorkspaces}
        />

        <FolderPickerDialog
          open={createFolderPickerOpen}
          onOpenChange={setCreateFolderPickerOpen}
          currentPath={resolveDefaultCreateFolderPath().folderPath}
          onSelect={handleCreateDocumentInFolder}
          mode="create"
          workspaces={moveDialogWorkspaces}
        />

        <DrivePublishPicker
          open={publishPickerOpen}
          onOpenChange={setPublishPickerOpen}
          documentTitle={openTabs.find((t) => t.id === activeTabId)?.title || 'Untitled'}
          organizationId={publishOrgId}
          onPublish={(folderId, folderLabel) => {
            void handlePublishToDrive(folderId, folderLabel)
          }}
          publishing={isPublishingToDrive}
        />

        {/* Workspace link dialog — shown when Open in Cursor finds project_not_linked */}
        {workspaceLinkContext && (
          <WorkspaceLinkDialog
            open={workspaceLinkDialogOpen}
            onOpenChange={setWorkspaceLinkDialogOpen}
            projectName={workspaceLinkContext.projectName}
            basecampProjectId={workspaceLinkContext.basecampProjectId}
            onLinked={() => {
              if (!basecampTodoId || !projectName) return
              handleOpenInCursor(basecampTodoId, projectName, basecampProjectId, activeTabId)
            }}
          />
        )}

        {/* Safety net: toast / menu events dispatch basecamp-project-configure-git */}
        {basecampProjectId ? (
          <div className="hidden" aria-hidden>
            <GitPathConfig
              projectId={basecampProjectId}
              projectName={projectName || basecampTodoTitle || 'Project'}
              variant="badge"
            />
          </div>
        ) : null}

        {/* Rename Dialog */}
        {currentDocumentPath && (
          <RenameDialog
            open={renameDialogOpen}
            onOpenChange={setRenameDialogOpen}
            type="file"
            currentName={currentDocumentPath.split('/').pop() || ''}
            documentTitle={openTabs.find(tab => tab.id === activeTabId)?.title}
            documentId={
              documentDbId ||
              (activeTabId && DOC_UUID_RE.test(activeTabId) ? activeTabId : null)
            }
            frontmatter={documentFrontmatter}
            pathFallback={currentDocumentPath}
            onRename={handleRenameConfirm}
          />
        )}

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  Are you sure you want to delete <strong className="text-foreground">"{documentToDelete?.title}"</strong>?
                </p>
                <p className="mt-3">
                  This will remove the document from your workspace and mark it as deleted.
                  You can undo this action from the notification.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                variant="destructive"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={!!watchLiveAgentId}
          onOpenChange={(open) => {
            if (!open) setWatchLiveAgentId(null)
          }}
        >
          <DialogContent
            className="max-w-2xl"
            data-testid={testIds.inbox.agentWatchLiveDialog}
          >
            <DialogHeader>
              <DialogTitle>Cloud agent live</DialogTitle>
            </DialogHeader>
            {watchLiveAgentId ? (
              <AgentLiveStreamPanel
                agentId={watchLiveAgentId}
                organizationId={tabForCloudDispatch?.workspaceId}
              />
            ) : null}
          </DialogContent>
        </Dialog>

        {gdocLinkedId ? (
          <DiffPreviewDialog
            open={gdocSync.diffOpen}
            onOpenChange={gdocSync.setDiffOpen}
            original={gdocSync.driveMarkdown}
            proposed={gdocSync.localMarkdown}
            filename="Google Drive"
            conflictMode
            conflictVariant="drive"
            leftUpdatedAt={gdocSync.driveModifiedTime}
            rightUpdatedAt={gdocSync.localModifiedTime}
            onAcceptUpstream={() => void gdocSync.acceptDrive()}
            onKeepFlowNotes={() => void gdocSync.keepMaater()}
            onCancel={() => void gdocSync.cancelSourceOfTruth()}
            isApplying={gdocSync.resolving}
            onAccept={() => {}}
            onReject={() => void gdocSync.cancelSourceOfTruth()}
          />
        ) : null}

      </div>
      </div>
    </div>
    </InboxThreadChromeProvider>
  )
}
